import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { Badge, Button, Modal, Input, Select, Label, TogglePill } from "../../components/ui";
import Icon from "../../components/Icon";
import PageHeader from "../../components/PageHeader";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useFocusField } from "../../hooks/useFocusField";
import { authApi, widgetApi, mcpApi, credentialsApi, voiceApi, agentApi, complianceApi, billingApi, llmApi, brandingApi } from "../../api/client";
import Logo from "../../components/Logo";
import { summarizeAgentUsage } from "./billingAgentUsage";
import { buildStdioServerFields, stdioReady, applyStdioPreset } from "./mcpStdioForm";
import { createLogger } from "../../utils/logger";
import { ADMIN_ROLES, OWNER_ROLES } from "../../utils/helpers";
import WebhooksView from "../webhooks/WebhooksView";
import IngestionView from "../ingestion/IngestionView";
import DataplexView from "./DataplexView";
import OpenApiIntegrationsTab from "./OpenApiIntegrationsTab";
import ModelServingTab from "./ModelServingTab";
import OutboundVoiceTab from "./OutboundVoiceTab";
import VoiceTrunksTab from "./VoiceTrunksTab";
import MLExecutionTargetsTab from "./MLExecutionTargetsTab";
import RbacPermissionMatrix from "./RbacPermissionMatrix";
import MLForecastingTab from "./MLForecastingTab";
import PhoneNumbersTab from "./PhoneNumbersTab";
// D-39: the Local LLM tab now mounts the full BYOM surface
// (LocalLLMEndpointsSection) — endpoint wizard + inline slug validation +
// starter templates + the OTel-2.0 on-demand serving light. The stripped
// LocalLlmEndpointsTab (which had the serving light but had lost the
// wizard/validation — the regression) is removed. CloudFallbackToggleCard is
// a separate named export from the same file — its own doc comment says it's
// "mounted at the top of the LLM Rankings tab", but nothing ever imported it;
// it was dead code (built + unit-tested, never rendered). Imported below and
// rendered at the top of the Rankings tab (adminTab === "rankings").
import LocalLLMEndpointsSection, { CloudFallbackToggleCard } from "./LocalLLMEndpointsSection";
import { providerLabel, addLocalProviders, addLocalEmbeddingProviders, applyRankingUpdate, appendRankingEntry, modelOptionLabel } from "./rankingsModel";
import ExecutableSkillsTab from "./ExecutableSkillsTab";
import GuardrailsSafetyTab from "./GuardrailsSafetyTab";
import OutboundVoiceConsole from "./OutboundVoiceConsole";

const log = createLogger("AdminView");

const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

// Quick-Connect tile array removed 2026-05-04. The platform now exposes a
// single generic register flow ("+ Add Server") that handles every MCP
// server through Phase E1's URL/header templating + OAuth-managed
// credential primitives. The previous vendor-specific tiles (slack,
// github, salesforce_devtools) baked in one specific implementation
// choice per vendor — community npm STDIO for Slack vs Slack's hosted
// MCP, etc. — and routinely led admins into mismatches between their
// actual token type and the tile's hardcoded auth shape. Three generic
// credential primitives (generic_api_key, generic_headers, generic_oauth)
// + URL/header templating cover all real-world cases without per-vendor
// code. handleEditServer() now reads templating config off the server
// row directly instead of resolving against a tile catalog.
const MCP_QUICK_CONNECT_INTEGRATIONS = [];

const ADMIN_LOGS = [];

function BillingTab() {
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const bp = useBreakpoint();

  useEffect(() => {
    setLoading(true);
    billingApi.usage().then(r => { if (r.success) setBillingData(r.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim }}>Loading billing data...</div>;
  if (!billingData) return <div style={{ padding: 40, textAlign: "center", color: COLORS.textDim }}>Unable to load billing data</div>;

  // B4: per-agent usage — normalized + sorted in the tested helper (assignment,
  // not an object-literal prop) so the render below stays thin host wiring.
  const agentUsage = summarizeAgentUsage(billingData.usage?.by_agent);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text }}>Billing & Usage</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
            Cycle started: {billingData.cycle_start?.slice(0, 10) || "—"}
          </div>
        </div>
        <div style={{ fontSize: 11, color: COLORS.textMuted, padding: "8px 14px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface }}>
          Contact Mirrorsme for pricing
        </div>
      </div>



      {/* Usage meters */}
      <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(2, 1fr)", gap: 14, marginBottom: 24 }}>
        {(billingData.meters || []).map(m => (
          <div key={m.key} style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted }}>{m.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: COLORS.text, letterSpacing: "-0.02em", marginTop: 6 }}>
              {m.used.toLocaleString()}
              {m.unit ? <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginLeft: 6 }}>{m.unit}</span> : null}
            </div>
          </div>
        ))}
      </div>

      {/* Usage by provider */}
      {billingData.usage?.by_provider && Object.keys(billingData.usage.by_provider).length > 0 && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 14 }}>Usage by Provider</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            {Object.entries(billingData.usage.by_provider).map(([provider, data]) => {
              const label = {
                openai: "OpenAI",
                anthropic: "Anthropic",
                bedrock: "AWS Bedrock",
                gemini: "Gemini",
                deepgram: "Deepgram",
                elevenlabs: "ElevenLabs",
              }[provider] || provider;
              return (
                <div key={provider} style={{ padding: 12, borderRadius: RADIUS.deep, background: COLORS.surface, border: `1px solid ${COLORS.border}`, boxShadow: MATERIALS.spatialShadow(), textAlign: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.accentText, marginTop: 4 }}>{data.calls?.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>calls</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, marginTop: 4 }}>{(data.tokens / 1000).toFixed(1)}K</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>tokens</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Usage by agent */}
      {agentUsage.length > 0 && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px", marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 14 }}>Usage by Agent</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: "left", color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>
                  <th style={{ padding: "8px 10px", fontWeight: 600 }}>Agent</th>
                  <th style={{ padding: "8px 10px", fontWeight: 600, textAlign: "right" }}>API Calls</th>
                  <th style={{ padding: "8px 10px", fontWeight: 600, textAlign: "right" }}>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {agentUsage.map(a => (
                  <tr key={a.agentId || a.agentName} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "8px 10px", color: COLORS.text, fontWeight: 600 }}>{a.agentName}</td>
                    <td style={{ padding: "8px 10px", color: COLORS.accentText, fontWeight: 700, textAlign: "right" }}>{a.apiCalls.toLocaleString()}</td>
                    <td style={{ padding: "8px 10px", color: COLORS.text, textAlign: "right" }}>{a.tokensLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function ComplianceTab({ tenantId }) {
  const [retentionStats, setRetentionStats] = useState(null);
  const [retentionDays, setRetentionDays] = useState(365);
  const [retentionResult, setRetentionResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("json");
  const [report, setReport] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [dsarProfileId, setDsarProfileId] = useState("");
  const [dsarLoading, setDsarLoading] = useState(false);

  useEffect(() => {
    complianceApi.retentionStats().then(r => { if (r.success) setRetentionStats(r.data); }).catch(() => {});
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await complianceApi.exportLogs(exportFormat);
      if (exportFormat === "csv" || typeof res === "string") {
        // Download as file
        const blob = new Blob([typeof res === "string" ? res : JSON.stringify(res.data, null, 2)], { type: exportFormat === "csv" ? "text/csv" : "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `audit_logs.${exportFormat}`; a.click();
        URL.revokeObjectURL(url);
      } else if (res.success) {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "audit_logs.json"; a.click();
        URL.revokeObjectURL(url);
      }
    } catch {} finally { setExporting(false); }
  };

  const handleRetention = async (dryRun) => {
    try {
      const res = await complianceApi.applyRetention(retentionDays, dryRun);
      if (res.success) setRetentionResult(res.data);
    } catch {}
  };

  const handleReport = async (type) => {
    setReportType(type);
    setReportLoading(true);
    setReport(null);
    try {
      const res = type === "soc2" ? await complianceApi.soc2Report() : await complianceApi.gdprReport();
      if (res.success) setReport(res.data);
    } catch {} finally { setReportLoading(false); }
  };

  return (
    <div>
      {/* Export Section */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Audit Trail Export</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <select value={exportFormat} onChange={e => setExportFormat(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
          </select>
          <Button onClick={handleExport} disabled={exporting}
            style={{ background: COLORS.accent, border: "none", color: "#fff" }}>
            {exporting ? "Exporting..." : "Export Audit Logs"}
          </Button>
        </div>
      </div>

      {/* Retention Policy */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Retention Policy</div>
        {retentionStats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
            <div style={{ padding: 12, borderRadius: RADIUS.deep, background: COLORS.surface, border: `1px solid ${COLORS.border}`, boxShadow: MATERIALS.spatialShadow(), textAlign: "center" }}>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>Total Entries</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text }}>{retentionStats.total_entries?.toLocaleString()}</div>
            </div>
            <div style={{ padding: 12, borderRadius: RADIUS.deep, background: COLORS.surface, border: `1px solid ${COLORS.border}`, boxShadow: MATERIALS.spatialShadow(), textAlign: "center" }}>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>Last 30 Days</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text }}>{retentionStats.age_buckets?.["30d"]?.toLocaleString()}</div>
            </div>
            <div style={{ padding: 12, borderRadius: RADIUS.deep, background: COLORS.surface, border: `1px solid ${COLORS.border}`, boxShadow: MATERIALS.spatialShadow(), textAlign: "center" }}>
              <div style={{ fontSize: 10, color: COLORS.textDim }}>Oldest Entry</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted }}>{retentionStats.oldest_entry?.slice(0, 10) || "—"}</div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>Retain logs for</span>
          <input type="number" value={retentionDays} onChange={e => setRetentionDays(parseInt(e.target.value) || 365)}
            min={30} max={3650}
            style={{ width: 80, padding: "8px 10px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13, textAlign: "center" }} />
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>days</span>
          <Button onClick={() => handleRetention(true)} variant="secondary" size="sm">Preview</Button>
          <Button onClick={() => handleRetention(false)} size="sm"
            style={{ background: COLORS.accent, color: "#fff", border: "none" }}>
            Apply
          </Button>
        </div>
        {retentionResult && (
          <div style={{
            padding: 12, borderRadius: 8, fontSize: 12,
            background: retentionResult.dry_run ? `${COLORS.orange}10` : `${COLORS.green}10`,
            border: `1px solid ${retentionResult.dry_run ? COLORS.orange : COLORS.green}25`,
            color: retentionResult.dry_run ? COLORS.orange : COLORS.green,
          }}>
            {retentionResult.dry_run ? "Preview: " : "Applied: "}
            {retentionResult.affected_rows} entries {retentionResult.dry_run ? "would be" : "were"} purged
            (older than {retentionResult.cutoff_date?.slice(0, 10)})
          </div>
        )}
      </div>

      {/* Compliance Reports */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Compliance Reports</div>
        {/* Report-generation buttons share a single secondary treatment.
            Earlier they used 3 different accent colors (indigo / green /
            orange) for semantically equivalent actions, which read as
            "different states" when there is no state distinction —
            they're all "click to download a report". */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <Button onClick={() => handleReport("soc2")} variant="secondary">
            SOC2 Report
          </Button>
          <Button onClick={() => handleReport("gdpr")} variant="secondary">
            GDPR Data Inventory
          </Button>
        </div>

        {/* DSAR — Data Subject Access Request */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Data Subject Access Request (DSAR)</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="text" value={dsarProfileId} onChange={e => setDsarProfileId(e.target.value)}
              placeholder="Customer Profile ID"
              style={{ flex: 1, padding: "8px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
            <Button onClick={async () => {
              if (!dsarProfileId.trim()) return;
              setDsarLoading(true); setReport(null); setReportType("dsar");
              try {
                const res = await complianceApi.dsarReport(dsarProfileId.trim());
                if (res.success) setReport(res.data);
              } catch {} finally { setDsarLoading(false); }
            }} disabled={dsarLoading || !dsarProfileId.trim()}
              variant="secondary">
              {dsarLoading ? "..." : "Generate DSAR"}
            </Button>
          </div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Returns all personal data held for a specific customer profile</div>
        </div>

        {reportLoading && <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim }}>Generating report...</div>}

        {report && (
          <div style={{ padding: 16, borderRadius: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, maxHeight: 500, overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{report.report_type} Report</div>
              <Button size="sm" variant="secondary" onClick={() => {
                const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `${report.report_type.toLowerCase()}_report.json`; a.click();
                URL.revokeObjectURL(url);
              }}>Download JSON</Button>
            </div>
            <pre style={{ fontSize: 11, color: COLORS.textMuted, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {JSON.stringify(report, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}


function MigrateEmbeddingsPanel({ embeddingModels, providers }) {
  // Per-agent "Migrate embeddings" admin action (Track C.8). Lets an
  // admin re-embed a populated agent's corpus under a new model. While a
  // migration is running, the agent's ingest / delete / re-enrich
  // endpoints return HTTP 423 Locked — surfaced inline by the progress
  // bar and ETA below the agent picker.
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    agentApi.list().then(r => {
      if (r?.success && Array.isArray(r.data)) setAgents(r.data);
    }).catch(() => {});
  }, []);

  const refreshStatus = useCallback(async (id) => {
    if (!id) { setStatus(null); return; }
    try {
      const r = await authApi.getAgentEmbeddingMigration(id);
      if (r?.success) setStatus(r.data || null);
    } catch (e) {
      setStatus(null);
    }
  }, []);

  useEffect(() => { refreshStatus(agentId); }, [agentId, refreshStatus]);

  // Poll while migrating
  useEffect(() => {
    if (status?.state?.status !== "migrating") return;
    const t = setInterval(() => refreshStatus(agentId), 4000);
    return () => clearInterval(t);
  }, [status?.state?.status, agentId, refreshStatus]);

  const availableProviders = providers.filter(p => embeddingModels[p]?.length);

  // Reset model when provider changes. Batch 10 changed embeddingModels
  // shape from string[] to {id, kind, badges?}[] — handle both for
  // backward-compat with any caller still passing string arrays.
  const _modelId = (m) => (typeof m === "string" ? m : m?.id);
  useEffect(() => {
    const first = embeddingModels[provider]?.[0];
    setModel(_modelId(first) || "");
  }, [provider, embeddingModels]);

  const handleStart = async () => {
    if (!agentId || !provider || !model) return;
    setBusy(true);
    setErr(null);
    try {
      await authApi.migrateAgentEmbeddings(agentId, provider, model);
      refreshStatus(agentId);
    } catch (e) {
      setErr(e?.detail?.message || e?.message || "Failed to start migration");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await authApi.cancelAgentEmbeddingMigration(agentId);
      refreshStatus(agentId);
    } catch (e) {
      setErr(e?.message || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  const state = status?.state || {};
  const isRunning = state.status === "migrating";
  const total = state.total_docs || 0;
  const processed = state.processed_docs || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const eta = status?.eta_seconds;
  const etaLabel = eta == null ? null : (eta > 60 ? `${Math.round(eta / 60)} min` : `${Math.round(eta)} s`);

  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "16px 20px" }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>Migrate Embeddings</div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12, lineHeight: 1.6 }}>
        Switch an agent's embedding model. Re-embeds every chunk in the corpus under the new model and atomically flips the agent's lock when done. Existing chunks remain queryable until the migration completes. Ingest, delete, and re-enrich are blocked (HTTP 423) for that agent while migration runs.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>Agent</div>
          <select value={agentId} onChange={e => setAgentId(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, minWidth: 200 }}>
            <option value="">Select agent…</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>Target provider</div>
          <select value={provider} onChange={e => setProvider(e.target.value)}
            style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text }}>
            <option value="">Select…</option>
            {availableProviders.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 4 }}>Target model</div>
          <select value={model} onChange={e => setModel(e.target.value)} disabled={!provider}
            style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text, minWidth: 200 }}>
            {(embeddingModels[provider] || []).map(m => {
              const id = _modelId(m);
              return <option key={id} value={id}>{id}</option>;
            })}
          </select>
        </div>
        <Button onClick={handleStart} disabled={busy || isRunning || !agentId || !provider || !model}>
          {isRunning ? "Migration in progress…" : (busy ? "Starting…" : "Start migration")}
        </Button>
        {isRunning && (
          <Button variant="secondary" onClick={handleCancel} disabled={busy}>Cancel</Button>
        )}
      </div>
      {agentId && status && (
        <div style={{ fontSize: 11, color: COLORS.textMuted }}>
          <div>Lock: <code style={{ color: COLORS.text }}>{status.embedding_model_lock || "—"}</code></div>
          <div>Status: <span style={{ color: state.status === "failed" ? "#ef4444" : COLORS.text }}>{state.status || "idle"}</span></div>
          {isRunning && (
            <>
              <div style={{ marginTop: 6, marginBottom: 4 }}>{processed} / {total} chunks ({pct}%) {etaLabel ? `· ETA ${etaLabel}` : ""}</div>
              <div style={{ width: "100%", height: 6, background: COLORS.border, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: COLORS.accent, transition: "width 200ms" }} />
              </div>
            </>
          )}
          {state.error && <div style={{ marginTop: 6, color: "#ef4444" }}>Error: {state.error}</div>}
        </div>
      )}
      {err && <div style={{ marginTop: 8, fontSize: 11, color: "#ef4444" }}>{err}</div>}
    </div>
  );
}



function SuperAgentTab() {
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const handleBootstrap = async () => {
    setBootstrapping(true);
    setBootstrapResult(null);
    try {
      const res = await agentApi.bootstrapSuperAgent();
      setBootstrapResult(res.data);
    } catch (err) {
      setBootstrapResult({ error: err.message || "Failed to bootstrap" });
    } finally {
      setBootstrapping(false);
    }
  };
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Super Agent</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
          The Agent Builder is an AI-powered agent that creates other agents through natural language conversation.
        </div>
      </div>

      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
        padding: "28px 28px", marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24,
          }}>{"\uD83E\uDD16"}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>Agent Builder</div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
              A specialized AI agent that understands the entire I² (I-square) platform. Users describe what they need in plain English
              and it designs, configures, and creates agents with the right LLM provider, guardrails, MCP tools, escalation rules,
              and multi-agent orchestration — all through a simple chat conversation.
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>
              <strong style={{ color: COLORS.text }}>Capabilities:</strong> System prompt generation, MCP server matching,
              intelligent defaults, multi-agent orchestration design, per-channel handoff configuration, guardrail tuning
            </div>
            <button
              onClick={handleBootstrap}
              disabled={bootstrapping}
              style={{
                padding: "10px 24px", borderRadius: 10, border: "none", cursor: bootstrapping ? "wait" : "pointer",
                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`, color: "#fff",
                fontSize: 13, fontWeight: 700,
                boxShadow: `0 4px 16px ${COLORS.accentGlow}`,
              }}
            >
              {bootstrapping ? "Provisioning..." : "Provision Super Agent"}
            </button>
          </div>
        </div>

        {bootstrapResult && (
          <div style={{
            marginTop: 16, padding: "12px 16px", borderRadius: 10,
            background: bootstrapResult.error ? `${COLORS.pink}12` : `${COLORS.green}12`,
            border: `1px solid ${bootstrapResult.error ? COLORS.pink : COLORS.green}25`,
            fontSize: 12, color: bootstrapResult.error ? COLORS.pink : COLORS.green,
          }}>
            {bootstrapResult.error
              ? `Error: ${bootstrapResult.error}`
              : `${bootstrapResult.message}${bootstrapResult.id ? ` (ID: ${bootstrapResult.id})` : ""}`}
          </div>
        )}
      </div>

      <div style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
        padding: "20px 24px",
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>How It Works</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[
            { step: "1", title: "User opens web chat with the Agent Builder", desc: "Available via the chat widget or the Agents list" },
            { step: "2", title: "Describes what they need in natural language", desc: "\"Create a billing support agent for WhatsApp that escalates complaints\"" },
            { step: "3", title: "Super Agent discovers available resources", desc: "Calls tools to list models, MCP servers, and existing agents" },
            { step: "4", title: "Designs the agent configuration", desc: "Generates system prompt, selects model, attaches tools, configures guardrails" },
            { step: "5", title: "Presents a summary for review", desc: "Shows the full config and asks for confirmation before creating" },
            { step: "6", title: "Creates the agent on confirmation", desc: "Calls the platform API to create the agent as a draft" },
          ].map(s => (
            <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{
                width: 24, height: 24, borderRadius: 8, flexShrink: 0,
                background: `${COLORS.accent}15`, color: COLORS.accentText,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700,
              }}>{s.step}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{s.title}</div>
                <div style={{ fontSize: 11, color: COLORS.textDim }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const AdminView = ({ currentUser, currentTenant, refreshTenant }) => {
  const bp = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);
  // Default tab: Users for owners, API Keys for everyone else (since
  // the Users tab is owner-only and we hide it from non-owners below).
  const [adminTab, setAdminTab] = useState(
    OWNER_ROLES.includes(currentUser?.role) ? "users" : "apikeys"
  );
  // Deep-link target from ?focus=<field_name> — scrolled + focused after
  // the relevant tab / modal renders (useFocusField does the polling).
  const [focusedField, setFocusedField] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("business_user");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  // Visibility toggle for the user list — useful for screen-share
  // privacy (hide names + emails on demand). Default visible.
  const [usersListVisible, setUsersListVisible] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("All");
  const [logSearch, setLogSearch] = useState("");
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", status: "" });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("API Key");
  const [newKeyScopes, setNewKeyScopes] = useState(["platform"]);
  const [createdKey, setCreatedKey] = useState(null);
  const [createdKeyScopes, setCreatedKeyScopes] = useState([]);
  const [createKeyLoading, setCreateKeyLoading] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState(null);
  const [deleteKeyLoading, setDeleteKeyLoading] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [showAddServer, setShowAddServer] = useState(false);
  // mcpForm.provider — required for tile-driven registration so the vault
  //   picker filters correctly. Set from tile id (which matches the vault's
  //   provider key in services/credential_providers.py PROVIDERS).
  // mcpForm.credential_id — vault entry the server points at. Required for
  //   any provider that has fields (i.e. non-public APIs). Per "vault-only"
  //   invariant: inline config is no longer accepted.
  // MCP-UI-1: stdio_runtime (npx|uvx|docker|custom) + command + args_text let
  // Python/Docker/binary MCP servers be configured, not just npm. preset* carry
  // the suggested env-var keys/provider after a one-click preset (e.g. Postgres).
  const [mcpForm, setMcpForm] = useState({ server_id: "", name: "", url: "", description: "", connection_type: "http", package: "", provider: "", credential_id: "", url_template: "", header_template_text: "", stdio_runtime: "npx", command: "", args_text: "", presetEnvKeys: [], presetCredentialProvider: null, presetHint: "" });
  const [stdioPresets, setStdioPresets] = useState([]);  // GET /api/mcp/stdio-presets
  const [mcpSaving, setMcpSaving] = useState(false);
  const [mcpRegResult, setMcpRegResult] = useState(null); // {type: "success"|"error", message: "..."}
  const [mcpDeleting, setMcpDeleting] = useState(null);
  const [mcpHealth, setMcpHealth] = useState({});
  const [mcpTools, setMcpTools] = useState({});  // server_id → tools[]
  const [mcpToolsLoading, setMcpToolsLoading] = useState(null);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [intgForm, setIntgForm] = useState({});
  // ─── Milestone G: MCP infrastructure + credentials state ─────
  const [mcpInfra, setMcpInfra] = useState(null);         // TenantMCP status from GET /api/mcp/status
  const [mcpInfraLoading, setMcpInfraLoading] = useState(false);
  const [mcpProvisioning, setMcpProvisioning] = useState(false);
  const [mcpReconcilingNow, setMcpReconcilingNow] = useState(false);
  const [credProviders, setCredProviders] = useState([]);  // GET /api/mcp/credential-providers
  const [credentials, setCredentials] = useState([]);      // GET /api/mcp/credentials
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [credForm, setCredForm] = useState({ name: "", provider: "generic_api_key", description: "", values: {}, credential_type: "static", oauth_config: null });
  // Phase E1 — UI state for the OAuth Test Refresh button (per-credential).
  const [credOauthTesting, setCredOauthTesting] = useState(null);
  const [credOauthResult, setCredOauthResult] = useState(null);
  const [credSaving, setCredSaving] = useState(false);
  const [credEditingId, setCredEditingId] = useState(null);
  const [credDeleting, setCredDeleting] = useState(null);  // id being deleted
  const [credResult, setCredResult] = useState(null);      // inline result banner
  const [intgSaving, setIntgSaving] = useState(false);
  const [intgTesting, setIntgTesting] = useState(false);
  const [intgTestResult, setIntgTestResult] = useState(null);
  const [intgError, setIntgError] = useState(null);
  const [intgTestFailed, setIntgTestFailed] = useState({});
  // OpenObserve credentials modal state (separate from `intgForm` so the
  // plaintext password is only held in memory while the reveal modal is
  // open; it's cleared when the integration modal closes).
  const [ooProvisioning, setOoProvisioning] = useState(false);
  const [ooCreds, setOoCreds] = useState(null);    // { email, password, org, login_url } when revealed
  const [ooCredsLoading, setOoCredsLoading] = useState(false);
  const [ooCredsError, setOoCredsError] = useState(null);
  // LLM Rankings
  const [llmRankings, setLlmRankings] = useState({});
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsSaving, setRankingsSaving] = useState(null);
  const [dynamicModels, setDynamicModels] = useState(null);
  const [rankingsSaved, setRankingsSaved] = useState(null);

  const fetchIntegrations = async () => {
    try {
      const res = await authApi.getIntegrations();
      if (res.success) setIntegrations(res.data);
    } catch (err) {
      log.error("Failed to fetch integrations:", err);
    }
  };

  const fetchApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const res = await widgetApi.listKeys();
      if (res.success) setApiKeys(res.data);
    } catch (err) {
      log.error("Failed to fetch API keys:", err);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateKey = async () => {
    setCreateKeyLoading(true);
    try {
      const res = await widgetApi.createKey(newKeyName, newKeyScopes);
      if (res.success) {
        setCreatedKey(res.data.key);
        setCreatedKeyScopes(res.data.scopes || []);
        setNewKeyName("API Key");
        setNewKeyScopes(["platform"]);
        fetchApiKeys();
      }
    } catch (err) {
      log.error("Failed to create API key:", err);
    } finally {
      setCreateKeyLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    setRevokeLoading(true);
    try {
      await widgetApi.revokeKey(keyId);
      setRevokeConfirm(null);
      fetchApiKeys();
    } catch (err) {
      log.error("Failed to revoke API key:", err);
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleDeleteKey = async (keyId) => {
    setDeleteKeyLoading(true);
    try {
      await widgetApi.deleteKey(keyId);
      setDeleteConfirmKey(null);
      fetchApiKeys();
    } catch (err) {
      log.error("Failed to delete API key:", err);
    } finally {
      setDeleteKeyLoading(false);
    }
  };

  const fetchMcpServers = async () => {
    setMcpLoading(true);
    try {
      const res = await mcpApi.listServers();
      if (res.success) setMcpServers(res.data?.servers || []);
    } catch (err) {
      log.error("Failed to fetch MCP servers:", err);
    } finally {
      setMcpLoading(false);
    }
  };

  const fetchMcpHealth = async () => {
    try {
      const res = await mcpApi.health();
      if (res.success) {
        const healthMap = {};
        (res.data?.servers || []).forEach(s => { healthMap[s.server_id] = s; });
        setMcpHealth(healthMap);
      }
    } catch (err) {
      log.error("Failed to fetch MCP health:", err);
    }
  };

  // ─── Milestone G: Infrastructure + credentials fetchers ─────

  const fetchMcpInfra = async () => {
    setMcpInfraLoading(true);
    try {
      const res = await mcpApi.status();
      if (res.success) setMcpInfra(res.data);
    } catch (err) {
      log.error("Failed to fetch MCP infra status:", err);
    } finally {
      setMcpInfraLoading(false);
    }
  };

  const fetchCredentials = async () => {
    setCredentialsLoading(true);
    try {
      const res = await credentialsApi.list();
      if (res.success) setCredentials(res.data?.credentials || []);
    } catch (err) {
      log.error("Failed to fetch credentials:", err);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const fetchCredProviders = async () => {
    try {
      const res = await credentialsApi.listProviders();
      if (res.success) setCredProviders(res.data?.providers || []);
    } catch (err) {
      log.error("Failed to fetch credential providers:", err);
    }
    // MCP-UI-1: STDIO runtime + preset catalogue (Postgres/GitHub/filesystem).
    try {
      const pres = await credentialsApi.stdioPresets();
      if (pres.success) setStdioPresets(pres.data?.presets || []);
    } catch (err) {
      log.error("Failed to fetch STDIO presets:", err);
    }
  };


  const handleProvisionMcp = async () => {
    setMcpProvisioning(true);
    try {
      const res = await mcpApi.provision();
      if (res.success) {
        await fetchMcpInfra();
      }
    } catch (err) {
      log.error("Provision MCP failed:", err);
    } finally {
      setMcpProvisioning(false);
    }
  };

  const handleTriggerReconcile = async () => {
    try {
      await mcpApi.triggerReconcile();
      await fetchMcpInfra();
    } catch (err) {
      log.error("Trigger reconcile failed:", err);
    }
  };

  const handleSaveCredential = async () => {
    if (!credForm.name || !credForm.provider) return;
    setCredSaving(true);
    setCredResult(null);
    try {
      // Phase E1 — split oauth_config out of `values` for oauth_managed creds.
      // Provider tile fields with credential_type='oauth_managed' bind to
      // oauth_config keys (TOKEN_URL/CLIENT_ID/CLIENT_SECRET/REFRESH_TOKEN/SCOPES);
      // they're sent in the dedicated oauth_config field, NOT in values.
      const isOauth = credForm.credential_type === "oauth_managed";
      const cleanedValues = Object.fromEntries(
        Object.entries(credForm.values || {}).filter(([, v]) => v !== "")
      );
      const cleanedOauthConfig = isOauth
        ? Object.fromEntries(Object.entries(credForm.oauth_config || {}).filter(([, v]) => v !== ""))
        : null;
      if (credEditingId) {
        await credentialsApi.update(credEditingId, {
          name: credForm.name,
          description: credForm.description,
          values: isOauth ? null : cleanedValues,
          oauth_config: isOauth ? cleanedOauthConfig : null,
        });
        setCredResult({ type: "success", message: `Credential "${credForm.name}" updated` });
      } else {
        await credentialsApi.create({
          name: credForm.name,
          provider: credForm.provider,
          description: credForm.description,
          credential_type: credForm.credential_type || "static",
          values: isOauth ? {} : cleanedValues,
          oauth_config: isOauth ? cleanedOauthConfig : null,
        });
        setCredResult({ type: "success", message: `Credential "${credForm.name}" created` });
      }
      await fetchCredentials();
      setShowAddCredential(false);
      setCredEditingId(null);
      setCredForm({ name: "", provider: "generic_api_key", description: "", values: {}, credential_type: "static", oauth_config: null });
    } catch (err) {
      log.error("Save credential failed:", err);
      setCredResult({ type: "error", message: err.message || "Save failed" });
    } finally {
      setCredSaving(false);
    }
  };

  const handleDeleteCredential = async (id, force = false) => {
    setCredDeleting(id);
    try {
      await credentialsApi.delete(id, force);
      setCredResult({ type: "success", message: "Credential deleted" });
      await fetchCredentials();
    } catch (err) {
      // 409 with dependent_servers → offer force delete
      if (err.status === 409 && err.detail?.dependent_servers) {
        const dep = err.detail.dependent_servers.join(", ");
        if (window.confirm(
          `This credential is used by: ${dep}.\n\nForce delete will unlink + disable those servers. Continue?`
        )) {
          return handleDeleteCredential(id, true);
        }
      } else {
        log.error("Delete credential failed:", err);
        setCredResult({ type: "error", message: err.message || "Delete failed" });
      }
    } finally {
      setCredDeleting(null);
    }
  };

  const handleEditCredentialStart = async (cred) => {
    // Fetch full record with masked values
    try {
      const res = await credentialsApi.get(cred.id);
      if (res.success) {
        setCredEditingId(cred.id);
        setCredForm({
          name: res.data.name,
          provider: res.data.provider,
          description: res.data.description || "",
          values: res.data.values || {},  // already masked (••••••••) from backend
          // Phase E1 — round-trip credential_type + masked oauth_config.
          credential_type: res.data.credential_type || "static",
          oauth_config: res.data.oauth_config || null,
        });
        setShowAddCredential(true);
      }
    } catch (err) {
      log.error("Load credential for edit failed:", err);
    }
  };

  const handleAddServer = async () => {
    const isStdio = (mcpForm.connection_type || "stdio") === "stdio";
    if (!mcpForm.server_id || !mcpForm.name) return;
    if (isStdio && !stdioReady(mcpForm)) return;
    if (!isStdio && !mcpForm.url) return;
    setMcpSaving(true);
    try {
      const payload = {
        server_id: mcpForm.server_id,
        name: mcpForm.name,
        connection_type: mcpForm.connection_type || "stdio",
        description: mcpForm.description || "",
        enabled: true,
      };
      // MCP-UI-1: runtime-aware stdio fields (package for npx; command+args
      // for uvx/docker/custom). HTTP/SSE keep url below.
      if (isStdio) Object.assign(payload, buildStdioServerFields(mcpForm));
      if (mcpForm.url) payload.url = mcpForm.url;
      // Vault-only path: pass credential_id when set. Backend rejects any
      // payload with inline `config` and no credential_id (see
      // routers/mcp.py register_server).
      if (mcpForm.credential_id) payload.credential_id = mcpForm.credential_id;
      // Phase E1 — URL/header templates. Only relevant for HTTP/SSE servers.
      if (!isStdio) {
        if (mcpForm.url_template && mcpForm.url_template.trim()) {
          payload.url_template = mcpForm.url_template.trim();
        }
        if (mcpForm.header_template_text && mcpForm.header_template_text.trim()) {
          try {
            payload.header_template = JSON.parse(mcpForm.header_template_text);
          } catch (e) {
            setMcpRegResult({ type: "error", message: `header_template is not valid JSON: ${e.message}` });
            setMcpSaving(false);
            return;
          }
        }
      }
      const res = await mcpApi.registerServer(payload);
      if (res.success) {
        const toolCount = res.data?.tools_discovered || 0;
        setMcpRegResult({ type: "success", message: `${mcpForm.name} registered successfully${toolCount ? ` — ${toolCount} tool${toolCount !== 1 ? "s" : ""} discovered` : ""}` });
        setMcpForm({ server_id: "", name: "", url: "", description: "", connection_type: "http", package: "", url_template: "", header_template_text: "", stdio_runtime: "npx", command: "", args_text: "", presetEnvKeys: [], presetCredentialProvider: null, presetHint: "" });
        fetchMcpServers();
        fetchMcpHealth();
        setTimeout(() => { setShowAddServer(false); setMcpRegResult(null); }, 2000);
      }
    } catch (err) {
      const msg = err.message || "Registration failed. Check your credentials and try again.";
      setMcpRegResult({ type: "error", message: msg });
      log.error("Failed to register MCP server:", err);
    } finally {
      setMcpSaving(false);
    }
  };

  const handleDeleteServer = async (serverId) => {
    setMcpDeleting(serverId);
    try {
      await mcpApi.unregisterServer(serverId);
      fetchMcpServers();
    } catch (err) {
      log.error("Failed to unregister MCP server:", err);
    } finally {
      setMcpDeleting(null);
    }
  };

  // Pre-fill the register form with the existing server's values, then open
  // it. POST /api/mcp/servers is an upsert keyed on server_id, so Save will
  // update in place. Used when a previous registration failed (Bifrost
  // rejected the config / credentials were wrong) and the user needs to fix
  // things without losing the row.
  //
  // For Quick Connect integrations (slack / salesforce / etc.), we look up
  // the configFields definition by server_id so the form shows the right
  // credential inputs. For custom servers (no matching entry), configFields
  // is empty and the user just edits the server_id / name / url / package.
  const handleEditServer = (server) => {
    const qc = MCP_QUICK_CONNECT_INTEGRATIONS.find(i => i.id === server.server_id);
    setMcpForm({
      server_id: server.server_id || "",
      name: server.name || qc?.name || "",
      url: server.url || "",
      description: server.description || qc?.desc || "",
      connection_type: server.connection_type || qc?.connection_type || "http",
      package: server.package || qc?.package || "",
      // Vault-only: provider key drives the credential dropdown filter
      // (matches PROVIDERS catalog by convention). Pre-select the
      // server's existing credential_id so Edit shows what it's
      // pointing at; admin can swap to another vault entry.
      provider: qc?.id || server.provider || "",
      credential_id: server.credential_id || "",
      // Phase E1 — round-trip url_template + header_template into the
      // form so admin can edit them. header_template is JSONB on the
      // server; serialize for the textarea.
      url_template: server.url_template || "",
      header_template_text: server.header_template
        ? JSON.stringify(server.header_template, null, 2)
        : "",
    });
    setMcpRegResult(null);
    setShowAddServer(true);
  };

  const handleReconcileServers = async () => {
    setMcpRegResult({ type: "info", message: "Reconciling with Bifrost..." });
    try {
      const res = await mcpApi.reconcile();
      const { registered, failed } = res.data || {};
      setMcpRegResult({
        type: failed ? "error" : "success",
        message: `Reconciled: ${registered} registered, ${failed} failed.`,
      });
      fetchMcpServers();
      fetchMcpHealth();
    } catch (err) {
      setMcpRegResult({ type: "error", message: err.message || "Reconcile failed" });
    }
  };

  // Trigger a fresh server-side reconciliation. Unlike handleTriggerReconcile
  // (which bumps the operator's CR annotation), this hits the API's new
  // /api/mcp/reconcile endpoint — runs the reconciler loop once for this
  // tenant and surfaces the counts. See design mcp-remediation-v1.md §13.15.
  const handleReconcileBifrostNow = async () => {
    setMcpReconcilingNow(true);
    setMcpRegResult(null);
    try {
      const res = await mcpApi.reconcile({ un_quarantine: false });
      const d = res.data || {};
      const summary = [
        `${d.registered ?? 0} registered`,
        `${d.unregistered ?? 0} unregistered`,
        `${d.failed ?? 0} failed`,
        (d.skipped_backoff ?? 0) > 0 ? `${d.skipped_backoff} in backoff` : null,
        (d.skipped_quarantined ?? 0) > 0 ? `${d.skipped_quarantined} quarantined` : null,
      ].filter(Boolean).join(", ");
      setMcpRegResult({
        type: (d.failed ?? 0) > 0 ? "error" : "success",
        message: `Reconciled in ${d.duration_seconds ?? "?"}s: ${summary}`,
      });
      fetchMcpServers();
      fetchMcpHealth();
    } catch (err) {
      setMcpRegResult({ type: "error", message: err.message || "Reconcile failed" });
    } finally {
      setMcpReconcilingNow(false);
    }
  };

  const handleDiscoverTools = async (serverId) => {
    // Toggle behavior — if the panel is already open for this server, close it.
    // Fixes "no collapse button" UX bug reported 2026-04-23; previously the
    // panel could only grow.
    if (mcpTools[serverId]) {
      setMcpTools(prev => {
        const next = { ...prev };
        delete next[serverId];
        return next;
      });
      return;
    }
    setMcpToolsLoading(serverId);
    try {
      const res = await mcpApi.discoverTools(serverId);
      if (res.success) {
        setMcpTools(prev => ({
          ...prev,
          [serverId]: { tools: res.data?.tools || [], server_info: res.data?.server_info || {} },
        }));
      }
    } catch (err) {
      log.error("Failed to discover tools:", err);
      setMcpTools(prev => ({ ...prev, [serverId]: [] }));
    } finally {
      setMcpToolsLoading(null);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await authApi.users();
      if (res.success) setTenantUsers(res.data);
    } catch (err) {
      log.error("Failed to fetch users:", err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await authApi.auditLogs(100);
      if (res.success) setAuditLogs(res.data);
    } catch (err) {
      log.error("Failed to fetch audit logs:", err);
    }
  };

  // Skip fetchUsers() for non-owners — backend returns 403 since
  // /api/auth/users is owner-only. Avoids a noisy console error
  // every time a non-owner opens the Admin page.
  useEffect(() => {
    if (OWNER_ROLES.includes(currentUser?.role)) {
      fetchUsers();
    }
    fetchAuditLogs();
    fetchIntegrations();
    fetchApiKeys();
  }, []);

  // Deep-link from an IntegrationConfigRequired 412 (see Notes/tenant-
  // settings-scoping-v1.md §6 Step 13). The URL carries:
  //   ?tab=<admin_tab>&card=<INTG_CONFIG_name>&focus=<field_name>
  // On change: switch tab, open the matching integration modal (if any),
  // and record the field name for useFocusField to scroll/focus after
  // render. The hook polls briefly so timing between tab-switch and
  // modal-mount doesn't matter.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const card = params.get("card");
    const focus = params.get("focus");
    if (tab) setAdminTab(tab);
    if (focus) setFocusedField(focus);
    if (tab === "apikeys" && card && integrations.length > 0) {
      const intg = integrations.find(i => i.name === card);
      if (intg) handleEditIntegration(intg);
    }
  }, [location.search, integrations.length]);

  // G-LINK — listen for cross-component deep-link events. Sibling
  // tabs (VoiceTrunksTab, OutboundVoiceTab) + cross-feature surfaces
  // (Campaign wizard voice block, Builder voice node) dispatch
  // `admin:goto-tab` with detail.id to navigate back to the Voice
  // Console without prop-drilling navigation through 4 layers.
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.id;
      if (id) setAdminTab(id);
    };
    window.addEventListener("admin:goto-tab", handler);
    return () => window.removeEventListener("admin:goto-tab", handler);
  }, []);

  // Apply the focus: useFocusField polls document.querySelector for the
  // matching [data-field="<field>"] element and scrolls + focuses it.
  useFocusField(focusedField, [adminTab]);
  useEffect(() => {
    if (adminTab !== "mcp") return;
    fetchMcpServers();
    fetchMcpHealth();
    fetchMcpInfra();
    fetchCredentials();
    fetchCredProviders();
  }, [adminTab]);

  // Phase-aware polling: 5s while Provisioning/Upgrading, 30s when Ready/Failed (§13.1).
  useEffect(() => {
    if (adminTab !== "mcp") return;
    const phase = mcpInfra?.status?.phase || null;
    const transient = ["Pending", "Provisioning", "Upgrading"].includes(phase);
    const interval = transient ? 5000 : 30000;
    const t = setInterval(() => { fetchMcpInfra(); }, interval);
    return () => clearInterval(t);
  }, [adminTab, mcpInfra?.status?.phase]);
  useEffect(() => {
    if (adminTab === "rankings") {
      setRankingsLoading(true);
      Promise.all([
        authApi.getLlmRankings().then(res => { if (res.success) setLlmRankings(res.data || {}); }),
        // Call /api/llm/models (NOT /api/auth/llm-models) — only the
        // former returns the {id, name, kind, badges} shape the rankings
        // UI needs to filter Embeddings & Search (kind="embedding") and
        // Document Parsing (badges contains "vision"). The legacy
        // /api/auth/llm-models endpoint goes through
        // llm_factory.list_all_models() which iterates chat providers
        // only (no Cohere/Voyage embeddings) and returns raw Model
        // objects without kind/badges metadata.
        llmApi.models().then(res => {
          if (res?.data) {
            const byProvider = {};
            for (const [provider, models] of Object.entries(res.data)) {
              // D-39 (BYOM_005): a registered local endpoint is a real
              // resource the tenant configured — it must stay selectable
              // in the rankings dropdown even when its live model list
              // came back empty (endpoint unreachable / not yet cached).
              // Cloud providers keep the original length gate: an empty
              // list there means the key is misconfigured, not "real but
              // temporarily unlisted".
              if (Array.isArray(models) && (models.length || provider.startsWith("local_"))) {
                byProvider[provider] = models;
              }
            }
            if (Object.keys(byProvider).length) setDynamicModels(byProvider);
          }
        }),
      ])
        .catch(err => log.error("Failed to load rankings:", err))
        .finally(() => setRankingsLoading(false));
    }
  }, [adminTab]);

  const handleInvite = async () => {
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const res = await authApi.invite(inviteEmail, inviteName, inviteRole);
      if (res.success) {
        setInviteResult({ success: true, message: res.data.email_sent ? "Invitation email sent!" : "Invite created. Share this link:", link: res.data.invite_link });
        setInviteEmail("");
        setInviteName("");
        setInviteRole("business_user");
        fetchUsers();
      }
    } catch (err) {
      setInviteResult({ success: false, message: err.message || "Failed to send invite" });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name, role: u.role, status: u.status || "active" });
    setEditError(null);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setEditLoading(true);
    setEditError(null);
    try {
      const changes = {};
      if (editForm.name !== editingUser.name) changes.name = editForm.name;
      if (editForm.role !== editingUser.role) changes.role = editForm.role;
      if (editForm.status !== (editingUser.status || "active")) changes.status = editForm.status;
      if (Object.keys(changes).length === 0) { setEditingUser(null); setEditLoading(false); return; }
      const res = await authApi.updateUser(editingUser.id, changes);
      if (res.success) { setEditingUser(null); fetchUsers(); }
    } catch (err) {
      setEditError(err.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async (u) => {
    setDeleteLoading(true);
    try {
      const res = await authApi.deleteUser(u.id);
      if (res.success) { setDeleteConfirm(null); fetchUsers(); }
    } catch (err) {
      setDeleteConfirm(null);
      alert(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Integration Config Definitions ───
  const INTG_CONFIG = {
    "OpenAI": {
      fields: [
        { key: "openai_api_key", label: "API Key", type: "password", placeholder: "sk-..." },
      ],
      load: async () => { const r = await authApi.getLlmConfig(); return { openai_api_key: "", _existing_masks: { openai_api_key: r.data?.api_key_masked || "" } }; },
      save: (d) => authApi.updateLlmConfig(d),
      test: () => authApi.testLlmConfig(),
    },
    "Anthropic": {
      fields: [
        { key: "anthropic_api_key", label: "API Key", type: "password", placeholder: "sk-ant-..." },
      ],
      load: async () => { const r = await authApi.getAnthropicConfig(); return { anthropic_api_key: "", _existing_masks: { anthropic_api_key: r.data?.api_key_masked || "" } }; },
      save: (d) => authApi.updateAnthropicConfig(d),
      test: () => authApi.testAnthropicConfig(),
    },
    "Google Vertex AI": {
      fields: [
        { key: "google_ai_api_key", label: "API Key", type: "password", placeholder: "AIza..." },
      ],
      load: async () => { const r = await authApi.getVertexConfig(); return { google_ai_api_key: "", _existing_masks: { google_ai_api_key: r.data?.api_key_masked || "" } }; },
      save: (d) => authApi.updateVertexConfig(d),
      test: () => authApi.testVertexConfig(),
    },
    "AWS Bedrock": {
      fields: [
        { key: "aws_bedrock_access_key_id", label: "Access Key ID", type: "password", placeholder: "AKIA..." },
        { key: "aws_bedrock_secret_access_key", label: "Secret Access Key", type: "password", placeholder: "wJal..." },
        { key: "aws_bedrock_region", label: "Region", type: "text", placeholder: "us-east-1" },
      ],
      load: async () => { const r = await authApi.getBedrockConfig(); const d = r.data || {}; return { aws_bedrock_access_key_id: "", aws_bedrock_secret_access_key: "", aws_bedrock_region: d.region || "us-east-1", _existing_masks: { aws_bedrock_access_key_id: d.access_key_id_masked || "", aws_bedrock_secret_access_key: d.secret_access_key_masked || "" } }; },
      save: (d) => authApi.updateBedrockConfig(d),
      test: () => authApi.testBedrockConfig(),
    },
    "Cohere": {
      fields: [
        { key: "cohere_api_key", label: "API Key", type: "password", placeholder: "Cohere API key" },
      ],
      load: async () => { const r = await authApi.getCohereConfig(); const d = r.data || {}; return { cohere_api_key: "", _existing_masks: { cohere_api_key: d.api_key_masked || (d.configured ? "••••" : "") } }; },
      save: (d) => authApi.updateCohereConfig(d),
      test: () => authApi.testCohereConfig(),
    },
    "Voyage AI": {
      fields: [
        { key: "voyage_api_key", label: "API Key", type: "password", placeholder: "pa-..." },
      ],
      load: async () => { const r = await authApi.getVoyageConfig(); const d = r.data || {}; return { voyage_api_key: "", _existing_masks: { voyage_api_key: d.api_key_masked || (d.configured ? "••••" : "") } }; },
      save: (d) => authApi.updateVoyageConfig(d),
      test: () => authApi.testVoyageConfig(),
    },
    "Deepseek": {
      // First-class BYOK provider for deepseek-v4-flash + deepseek-v4-pro.
      // OpenAI-compatible wire format; see docs/design/deepseek-provider-v1.md.
      // The DPA warning lives in the card body (rendered via the `notice` field
      // pattern used by other compliance-sensitive providers); admins must
      // confirm China data routing is acceptable before enabling Deepseek
      // for production tenants.
      fields: [
        { key: "deepseek_api_key", label: "API Key", type: "password", placeholder: "sk-..." },
      ],
      notice: "Customer data may be processed in China. Confirm your DPA with legal before enabling Deepseek for production workloads. Get a key at platform.deepseek.com — both deepseek-v4-flash and deepseek-v4-pro are billed against this account.",
      load: async () => { const r = await authApi.getDeepseekConfig(); const d = r.data || {}; return { deepseek_api_key: "", _existing_masks: { deepseek_api_key: d.api_key_masked || (d.configured ? "••••" : "") } }; },
      save: (d) => authApi.updateDeepseekConfig(d),
      test: () => authApi.testDeepseekConfig(),
    },
    "WhatsApp": {
      fields: [
        // webhook_base_url moved to the Platform card — it's cross-cutting
        // (Twilio voice / email / campaigns also consume it), so a 412 from
        // any of those deep-links there instead of surfacing on WhatsApp.
        //
        // Two providers supported (2026-05-10 v2.0.0): Meta Cloud API and
        // WaSender. The provider field selects which path the backend uses;
        // the relevant credentials are filled per provider. Fields not
        // applicable to the chosen provider can be left empty without
        // breaking validation. Backend `update_whatsapp_config` /
        // `test_whatsapp_config` honour `whatsapp_provider`.
        { key: "whatsapp_provider", label: "Provider", type: "text", placeholder: "meta | wasender (default: meta)" },
        // ── Meta Cloud API fields ──
        { key: "whatsapp_phone_number_id", label: "Phone Number ID (Meta)", type: "text", placeholder: "1234567890" },
        { key: "whatsapp_access_token", label: "Access Token (Meta)", type: "password", placeholder: "EAAx..." },
        { key: "whatsapp_verify_token", label: "Verify Token (Meta)", type: "text", placeholder: "my-verify-token" },
        { key: "whatsapp_app_secret", label: "App Secret (Meta)", type: "password", placeholder: "abc123..." },
        // ── WaSender fields ──
        { key: "wasender_phone_number", label: "Phone Number (WaSender)", type: "text", placeholder: "+1234567890" },
        { key: "wasender_api_token", label: "API Token (WaSender)", type: "password", placeholder: "wasender API token" },
        { key: "wasender_webhook_secret", label: "Webhook Secret (WaSender)", type: "password", placeholder: "for signature verification (optional)" },
      ],
      load: async () => {
        const r = await authApi.getWhatsAppConfig();
        const d = r.data || {};
        return {
          whatsapp_provider: d.provider || "meta",
          whatsapp_phone_number_id: d.phone_number_id || "",
          whatsapp_access_token: "",
          whatsapp_verify_token: d.verify_token || "",
          whatsapp_app_secret: "",
          wasender_phone_number: d.wasender_phone_number || "",
          wasender_api_token: "",
          wasender_webhook_secret: "",
          _existing_masks: {
            whatsapp_access_token: d.access_token || "",
            whatsapp_app_secret: d.app_secret || "",
            wasender_api_token: d.wasender_api_token || "",
            wasender_webhook_secret: d.wasender_webhook_secret || "",
          },
        };
      },
      save: (d) => authApi.updateWhatsAppConfig(d),
      test: () => authApi.testWhatsAppConfig(),
    },
    "Platform": {
      // Cross-cutting platform-level settings that aren't specific to one
      // integration. Holds webhook_base_url today; extensible to other
      // shared fields. Matches the backend's FIELD_TO_INTEGRATION routing.
      fields: [
        { key: "webhook_base_url", label: "Webhook Base URL", type: "text", placeholder: "https://your-domain.com" },
      ],
      load: async () => { const r = await authApi.getPlatformConfig(); const d = r.data || {}; return { webhook_base_url: d.webhook_base_url || "" }; },
      save: (d) => authApi.updatePlatformConfig(d),
      test: null,
    },
    "Twilio": {
      fields: [
        { key: "twilio_account_sid", label: "Account SID", type: "text", placeholder: "AC..." },
        { key: "twilio_auth_token", label: "Auth Token", type: "password", placeholder: "Your auth token" },
        {
          key: "twilio_phone_number",
          label: "SMS Phone Number",
          type: "text",
          placeholder: "+15551234567",
          // Multi-tenant voice inbound v1 (2026-07-17): this field is
          // now SMS-scoped only. Voice inbound routing uses the
          // tenant_phone_numbers table (Admin → Voice → Phone Numbers).
          helper: "Used for SMS webhooks. Voice inbound numbers are managed in Admin → Voice → Phone Numbers.",
        },
      ],
      load: async () => { const r = await authApi.getSMSConfig(); const d = r.data || {}; return { twilio_account_sid: d.account_sid || "", twilio_auth_token: "", twilio_phone_number: d.phone_number || "", _existing_masks: { twilio_auth_token: d.auth_token || "" } }; },
      save: (d) => authApi.updateSMSConfig(d),
      test: () => authApi.testSMSConfig(),
    },
    "Deepgram": {
      fields: [
        { key: "deepgram_api_key", label: "API Key", type: "password", placeholder: "Your Deepgram API key" },
      ],
      load: async () => { const r = await voiceApi.getConfig(); const d = r.data || {}; return { deepgram_api_key: "", _existing_masks: { deepgram_api_key: d.deepgram_api_key_masked || (d.deepgram_configured ? "••••" : "") } }; },
      save: (d) => voiceApi.updateConfig(d),
      test: () => voiceApi.testConfig(),
    },
    "ElevenLabs": {
      fields: [
        { key: "elevenlabs_api_key", label: "API Key", type: "password", placeholder: "Your ElevenLabs API key" },
        { key: "elevenlabs_voice_id", label: "Active Voice ID", type: "text", placeholder: "DODLEQrClDo8wCz460ld" },
        { key: "elevenlabs_model", label: "Model", type: "text", placeholder: "eleven_turbo_v2_5" },
        { key: "elevenlabs_stability", label: "Stability (0=expressive, 1=stable)", type: "number", placeholder: "0.35", step: "0.05" },
        { key: "elevenlabs_similarity_boost", label: "Similarity Boost", type: "number", placeholder: "0.80", step: "0.05" },
        { key: "elevenlabs_style", label: "Style (0=neutral, 1=emotional)", type: "number", placeholder: "0.4", step: "0.1" },
        { key: "elevenlabs_speaker_boost", label: "Speaker Boost", type: "checkbox" },
        { key: "elevenlabs_speed", label: "Speed", type: "number", placeholder: "1.05", step: "0.05" },
        { key: "_voice_pool", label: "Voice Pool", type: "custom" },
      ],
      load: async () => {
        const r = await voiceApi.getConfig(); const d = r.data || {};
        return {
          elevenlabs_api_key: "", elevenlabs_voice_id: d.elevenlabs_voice_id || "",
          elevenlabs_model: d.elevenlabs_model || "", elevenlabs_stability: d.elevenlabs_stability ?? "",
          elevenlabs_similarity_boost: d.elevenlabs_similarity_boost ?? "",
          elevenlabs_style: d.elevenlabs_style ?? "", elevenlabs_speaker_boost: d.elevenlabs_speaker_boost ?? true,
          elevenlabs_speed: d.elevenlabs_speed ?? "",
          elevenlabs_voice_pool: d.elevenlabs_voice_pool || [],
          _existing_masks: { elevenlabs_api_key: d.elevenlabs_api_key_masked || (d.elevenlabs_configured ? "••••" : "") },
        };
      },
      save: (d) => voiceApi.updateConfig(d),
      test: () => voiceApi.testConfig(),
    },
    "SMTP / Email": {
      fields: [
        { key: "smtp_host", label: "SMTP Host", type: "text", placeholder: "smtp.gmail.com" },
        { key: "smtp_port", label: "Port", type: "number", placeholder: "587" },
        { key: "smtp_username", label: "Username", type: "text", placeholder: "user@example.com" },
        { key: "smtp_password", label: "Password", type: "password", placeholder: "App password or SMTP password" },
        { key: "smtp_from_email", label: "From Email", type: "text", placeholder: "noreply@yourdomain.com" },
        { key: "smtp_use_tls", label: "Use TLS", type: "checkbox" },
      ],
      load: async () => { const r = await authApi.getSmtpConfig(); const d = r.data || {}; return { smtp_host: d.host || "", smtp_port: d.port || 587, smtp_username: d.username || "", smtp_password: "", smtp_from_email: d.from_email || "", smtp_use_tls: d.use_tls ?? true, _existing_masks: { smtp_password: d.password || "" } }; },
      save: (d) => authApi.updateSmtpConfig(d),
      test: () => authApi.testSmtpConfig(),
    },
    "Firecrawl": {
      fields: [
        { key: "firecrawl_api_key", label: "API Key", type: "password", placeholder: "fc-..." },
      ],
      load: async () => { const r = await authApi.getFirecrawlConfig(); const d = r.data || {}; return { firecrawl_api_key: "", _existing_masks: { firecrawl_api_key: d.api_key || (d.configured ? "••••" : "") } }; },
      save: (d) => authApi.updateFirecrawlConfig(d),
      test: () => authApi.testFirecrawlConfig(),
    },
    // Hosted Model Serving Part II (R1): only gated/private repos need it. The
    // 412 from a gated lookup deep-links here (card "Hugging Face", field
    // huggingface_token — R2).
    "Hugging Face": {
      fields: [
        { key: "huggingface_token", label: "Access token", type: "password", placeholder: "hf_…",
          helper: "A read token from huggingface.co → Settings → Access Tokens, from an account that accepted the model's license. Used only to download weights." },
      ],
      load: async () => { const r = await authApi.getHuggingFaceConfig(); const d = r.data || {}; return { huggingface_token: "", _existing_masks: { huggingface_token: d.token || "" } }; },
      save: (d) => authApi.updateHuggingFaceConfig(d),
      test: () => authApi.testHuggingFaceConfig(),
      clear: { label: "Remove token", run: () => authApi.updateHuggingFaceConfig({ huggingface_token: "" }) },
    },
    "Genesys Cloud": {
      fields: [
        { key: "genesys_client_id", label: "Client ID", type: "password", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
        { key: "genesys_client_secret", label: "Client Secret", type: "password", placeholder: "xxxxxxxx..." },
        { key: "genesys_region", label: "Region", type: "text", placeholder: "us-east-1" },
        { key: "genesys_sip_trunk_uri", label: "SIP Trunk URI", type: "text", placeholder: "sip:+1234@trunk.genesys.com" },
        { key: "genesys_queue_id", label: "Default Queue ID", type: "text", placeholder: "queue-uuid" },
        { key: "genesys_open_messaging_id", label: "Open Messaging Integration ID", type: "text", placeholder: "integration-uuid" },
        { key: "handoff_provider", label: "Enable as Handoff Provider", type: "checkbox" },
      ],
      load: async () => {
        const r = await authApi.getGenesysConfig();
        const d = r.data || {};
        const g = d.genesys || {};
        return {
          genesys_client_id: "", genesys_client_secret: "",
          genesys_region: g.region || "us-east-1",
          genesys_sip_trunk_uri: g.sip_trunk_uri || "",
          genesys_queue_id: g.queue_id || "",
          genesys_open_messaging_id: g.open_messaging_id || "",
          handoff_provider: d.provider === "genesys",
          _existing_masks: { genesys_client_id: g.client_id || "", genesys_client_secret: g.client_secret || (d.configured ? "••••" : "") },
        };
      },
      save: async (d) => {
        const genesysFields = {};
        for (const k of ["genesys_client_id", "genesys_client_secret", "genesys_region", "genesys_sip_trunk_uri", "genesys_queue_id", "genesys_open_messaging_id"]) {
          if (d[k] !== undefined && d[k] !== "") genesysFields[k] = d[k];
        }
        await authApi.updateGenesysConfig(genesysFields);
        if (d.handoff_provider) {
          await authApi.setHandoffProvider("genesys");
        }
      },
      test: () => authApi.testGenesysConfig(),
    },
    "OpenObserve": {
      fields: [],
      readOnly: true,
      load: async () => {
        const r = await authApi.getOpenObserveConfig();
        const d = r.data || {};
        return {
          _dashboard_url: d.dashboard_url || "",
          _login_url: d.login_url || "",
          _org: d.org || "",
          _email: d.user_email || "",
          _password_set: !!d.password_set,
          _enabled: d.enabled,
        };
      },
      test: () => authApi.testOpenObserveConfig(),
    },
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setLogoUploading(true);
    setLogoError(null);
    try {
      await brandingApi.uploadLogo(file);
      await refreshTenant?.();
    } catch (err) {
      setLogoError(err.message || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUploading(true);
    setLogoError(null);
    try {
      await brandingApi.removeLogo();
      await refreshTenant?.();
    } catch (err) {
      setLogoError(err.message || "Failed to remove logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleEditIntegration = async (intg) => {
    const cfg = INTG_CONFIG[intg.name];
    if (!cfg) return;
    setEditingIntegration(intg);
    setIntgError(null);
    setIntgTestResult(null);
    try {
      const loaded = await cfg.load();
      setIntgForm(loaded);
    } catch {
      setIntgForm(Object.fromEntries(cfg.fields.map(f => [f.key, ""])));
    }
  };

  const handleSaveIntegration = async () => {
    const cfg = INTG_CONFIG[editingIntegration?.name];
    if (!cfg) return;
    setIntgSaving(true);
    setIntgError(null);
    try {
      // Filter out empty password fields (don't overwrite with blank)
      const payload = {};
      for (const f of cfg.fields) {
        const val = intgForm[f.key];
        if (f.type === "password" && !val) continue;
        if (f.type === "checkbox") { payload[f.key] = !!val; continue; }
        if (val !== undefined && val !== "") payload[f.key] = f.type === "number" ? Number(val) : val;
      }
      await cfg.save(payload);
      setEditingIntegration(null);
      fetchIntegrations();
    } catch (err) {
      setIntgError(err.message || "Failed to save");
    } finally {
      setIntgSaving(false);
    }
  };

  // Optional per-card "remove the saved credential" (e.g. Hugging Face → Remove token).
  const handleClearIntegration = async () => {
    const cfg = INTG_CONFIG[editingIntegration?.name];
    setIntgSaving(true);
    setIntgError(null);
    try {
      await cfg.clear.run();
      setEditingIntegration(null);
      fetchIntegrations();
    } catch (err) {
      setIntgError(err.message || "Failed to remove");
    } finally {
      setIntgSaving(false);
    }
  };

  const handleTestIntegration = async () => {
    const cfg = INTG_CONFIG[editingIntegration?.name];
    if (!cfg?.test) return;
    setIntgTesting(true);
    setIntgTestResult(null);
    try {
      const res = await cfg.test();
      setIntgTestResult({ success: true, message: res.data?.reply || res.data?.message || "Connection successful" });
      setIntgTestFailed(prev => ({ ...prev, [editingIntegration.name]: false }));
    } catch (err) {
      setIntgTestResult({ success: false, message: err.message || "Test failed" });
      setIntgTestFailed(prev => ({ ...prev, [editingIntegration.name]: true }));
    } finally {
      setIntgTesting(false);
    }
  };

  const formatRole = (r) => ({ owner: "Owner", administrator: "Administrator", developer: "Developer", supervisor: "Supervisor", business_user: "Business User" }[r] || r);
  const userRole = currentUser?.role ? formatRole(currentUser.role) : "Owner";
  const roleColor = { Owner: COLORS.pink, Administrator: COLORS.accent, Developer: COLORS.green, Supervisor: "#10B981", "Business User": COLORS.orange };
  // Backend gates OpenAPI CRUD on require_administrator (router A1).
  // Without the corresponding frontend hide, non-admins click in and
  // every action 403s with no graceful UX.
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);
  // Backend gates /api/auth/users on require_owner (tightened
  // 2026-05-10). Hide the Users & Roles tab for non-owners — admins,
  // developers, supervisors, business_users would otherwise see an
  // empty list because fetchUsers() returns 403.
  const isOwner = OWNER_ROLES.includes(currentUser?.role);
  const tabs = [
    ...(isOwner ? [{ id: "users", label: "Users & Roles", icon: "\uD83D\uDC65", shortLabel: "Users" }] : []),
    { id: "apikeys", label: "API Keys & Services", icon: "\uD83D\uDD11", shortLabel: "API Keys" },
    { id: "mcp", label: "MCP Servers", icon: "\uD83D\uDD0C", shortLabel: "MCP" },
    ...(isAdmin ? [{ id: "openapi", label: "OpenAPI Integrations", icon: "\uD83D\uDD17", shortLabel: "OpenAPI" }] : []),
    { id: "webhooks", label: "Webhooks", icon: "\u21C4", shortLabel: "Webhooks" },
    { id: "ingestion", label: "Pipeline Jobs", icon: "\u21F6", shortLabel: "Pipeline Jobs" },
    { id: "rankings", label: "LLM Rankings", icon: "\uD83C\uDFC6", shortLabel: "Rankings" },
    ...(isAdmin ? [{ id: "local_llm", label: "Local LLM", icon: "\uD83D\uDDA5\uFE0F", shortLabel: "Local LLM" }] : []),
    ...(isAdmin ? [{ id: "model_serving", label: "Model Serving", icon: "\uD83D\uDE80", shortLabel: "Serving" }] : []),
    { id: "dataplex", label: "Dataplex & Grounding", icon: "\uD83D\uDD0D", shortLabel: "Dataplex" },
    ...(isAdmin ? [{ id: "ml_execution", label: "ML Execution Targets", icon: "\uD83D\uDCC8", shortLabel: "ML" }] : []),
    ...(isAdmin ? [{ id: "ml_forecasting", label: "ML Forecasting", icon: "\uD83D\uDD2E", shortLabel: "Forecast" }] : []),
    { id: "billing", label: "Billing & Usage", icon: "\uD83D\uDCB3", shortLabel: "Billing" },
    { id: "logs", label: "Audit Logs", icon: "\uD83D\uDCCB", shortLabel: "Logs" },
    ...(isAdmin ? [{ id: "guardrails", label: "Guardrails & Safety", icon: "\uD83D\uDEA7", shortLabel: "Safety" }] : []),
    { id: "compliance", label: "Compliance", icon: "\uD83D\uDEE1", shortLabel: "Compliance" },
    // D7 + D2 \u2014 Outbound Voice surfaces (admin-only). Gated on isAdmin
    // because they carry tenant-level spend caps + carrier credentials.
    // G-LINK: Voice Console is the unified surface; the per-area
    // Outbound Voice + Voice Trunks tabs remain for focused editing.
    ...(isAdmin ? [{ id: "voice_console", label: "Voice Console", icon: "\uD83C\uDF9B", shortLabel: "Console" }] : []),
    ...(isAdmin ? [{ id: "outbound_voice", label: "Outbound Voice", icon: "\uD83D\uDCDE", shortLabel: "Voice" }] : []),
    ...(isAdmin ? [{ id: "voice_trunks", label: "Voice Trunks", icon: "\uD83D\uDD17", shortLabel: "Trunks" }] : []),
    ...(isAdmin ? [{ id: "phone_numbers", label: "Phone Numbers", icon: "\u260E\uFE0F", shortLabel: "Numbers" }] : []),
    ...(isAdmin ? [{ id: "executable_skills", label: "Executable Skills", icon: "\uD83E\uDDEA", shortLabel: "Sandbox" }] : []),
    { id: "super_agent", label: "Super Agent", icon: "\uD83E\uDD16", shortLabel: "Super Agent" },
  ];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: bp.isMobile ? "16px 12px 0" : "24px 28px 0", flexShrink: 0 }}>
        <PageHeader
          title="Admin"
          subtitle="Tenant settings, users, API keys, integrations, compliance"
        />
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: bp.isMobile ? "column" : "row", overflow: "hidden" }}>
      {/* Admin sidebar / horizontal tabs */}
      {bp.isMobile ? (
        <div style={{
          display: "flex", gap: 4, padding: "10px 12px", overflowX: "auto", flexShrink: 0,
          background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        }}>
          {tabs.map(t => (
            <TogglePill
              key={t.id}
              active={adminTab === t.id}
              onClick={() => setAdminTab(t.id)}
              leadingIcon={<span>{t.icon}</span>}
              style={{ flexShrink: 0 }}
            >
              {t.shortLabel}
            </TogglePill>
          ))}
        </div>
      ) : (
      <div className="panel-scroll" style={{
        width: bp.isTablet ? 190 : 230,
        ...MATERIALS.thickGlass(),
        borderRadius: 0,
        borderRight: `1px solid ${COLORS.border}`,
        borderTop: "none", borderBottom: "none", borderLeft: "none",
        padding: "20px 12px", flexShrink: 0,
        // The tab list can be taller than the viewport (18+ items) — scroll it
        // independently so the bottom items stay reachable (the parent row is
        // overflow:hidden). minHeight:0 lets this flex child actually shrink+scroll.
        overflowY: "auto", minHeight: 0,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16, paddingLeft: 12 }}>
          Admin Console
        </div>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setAdminTab(t.id)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10,
            padding: "11px 12px", borderRadius: 10, border: "none", cursor: "pointer",
            background: adminTab === t.id ? COLORS.surfaceHover : "transparent",
            color: adminTab === t.id ? COLORS.text : COLORS.textMuted,
            fontSize: 12, fontWeight: 500, textAlign: "left", marginBottom: 2,
            transition: "all 0.15s",
          }}>
            <span style={{ fontSize: 14 }}>{t.icon}</span>
            {bp.isTablet ? t.shortLabel : t.label}
            {adminTab === t.id && <div style={{
              marginLeft: "auto", width: 5, height: 5, borderRadius: "50%",
              background: COLORS.accent, boxShadow: `0 0 6px ${COLORS.accentGlow}`,
            }} />}
          </button>
        ))}
      </div>
      )}

      {/* Webhooks & Ingestion — full-panel (own layout/scroll) */}
      {adminTab === "webhooks" && (
        <WebhooksView currentUser={currentUser} />
      )}
      {adminTab === "ingestion" && (
        <IngestionView />
      )}
      {adminTab === "dataplex" && (
        <DataplexView />
      )}

      {/* Admin content — padded scroll area for built-in tabs */}
      {!["webhooks", "ingestion", "dataplex"].includes(adminTab) && (
      <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>

        {/* ===== USERS & ROLES ===== */}
        {adminTab === "users" && (
          <div>
            <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 24, flexDirection: bp.isMobile ? "column" : "row", gap: bp.isMobile ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Users & Roles</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Manage team access and permissions</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* Show / Hide users list toggle. */}
                <Button
                  variant="secondary"
                  onClick={() => setUsersListVisible(v => !v)}
                  title={usersListVisible ? "Hide the user list" : "Show the user list"}
                >
                  {usersListVisible ? "Hide users" : "Show users"}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => { setShowInviteModal(true); setInviteResult(null); }}
                >
                  + Invite User
                </Button>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: bp.isMobile ? 8 : 12, marginBottom: 24 }}>
              <div style={{
                background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
                padding: "16px 18px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <Badge color={COLORS.green} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Active</Badge>
                  <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{tenantUsers.filter(u => u.status === "active").length}</div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>Active members</div>
              </div>
              <div style={{
                background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
                padding: "16px 18px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <Badge color={COLORS.orange} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>Pending</Badge>
                  <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{tenantUsers.filter(u => u.status === "invited").length}</div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>Pending invitations</div>
              </div>
              {currentTenant && (
                <div style={{
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
                  padding: "16px 18px",
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Workspace</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>{currentTenant.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{currentTenant.slug}</div>
                </div>
              )}
            </div>

            {/* Users table */}
            <div className="table-scroll" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: bp.isMobile ? "16px 12px" : "20px 24px", overflowX: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2.5fr 1fr 1fr 1.2fr 1.2fr", gap: 0, minWidth: bp.isMobile ? 640 : "auto" }}>
                {["User", "Role", "Status", "Last Active", "Actions"].map(h => (
                  <div key={h} style={{
                    fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.06em",
                    textTransform: "uppercase", padding: "0 0 12px 0", borderBottom: `1px solid ${COLORS.border}`,
                  }}>{h}</div>
                ))}
                {usersListVisible && tenantUsers.map(u => {
                  const uRole = formatRole(u.role);
                  const isInvited = u.status === "invited";
                  const isYou = currentUser && u.id === currentUser.id;
                  return (
                    <React.Fragment key={u.id}>
                      <div style={{ padding: "14px 0", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${COLORS.border}` }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: 8,
                          background: isInvited
                            ? `linear-gradient(135deg, ${COLORS.orange}30, ${COLORS.orange}15)`
                            : `linear-gradient(135deg, ${COLORS.accent}40, ${COLORS.pink}40)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 700, color: isInvited ? COLORS.orange : COLORS.text,
                        }}>{getInitials(u.name)}</div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: isInvited ? COLORS.textMuted : COLORS.text }}>{u.name}</div>
                          <div style={{ fontSize: 10, color: COLORS.textDim }}>{u.email}</div>
                        </div>
                      </div>
                      <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                        <Badge color={roleColor[uRole] || COLORS.accent}>{uRole}</Badge>
                      </div>
                      <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{
                            width: 6, height: 6, borderRadius: "50%",
                            background: u.status === "suspended" ? COLORS.pink : isInvited ? COLORS.orange : COLORS.green,
                            boxShadow: `0 0 4px ${u.status === "suspended" ? COLORS.pink : isInvited ? COLORS.orange : COLORS.green}66`,
                          }} />
                          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
                            {u.status === "suspended" ? "Suspended" : isInvited ? "Invited" : "Active"}
                          </span>
                        </div>
                      </div>
                      <div style={{ padding: "14px 0", fontSize: 12, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                        {isYou ? "Now" : u.last_active_at ? (() => {
                          const diff = Date.now() - new Date(u.last_active_at).getTime();
                          const mins = Math.floor(diff / 60000);
                          if (mins < 1) return "Just now";
                          if (mins < 60) return `${mins}m ago`;
                          const hrs = Math.floor(mins / 60);
                          if (hrs < 24) return `${hrs}h ago`;
                          const days = Math.floor(hrs / 24);
                          return `${days}d ago`;
                        })() : "\u2014"}
                      </div>
                      <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                        {isYou && <span style={{ fontSize: 10, color: COLORS.textDim, marginRight: 2 }}>You</span>}
                        <Button variant="secondary" size="sm" onClick={() => handleEditUser(u)}>Edit</Button>
                        {/* Delete is allowed for owners now that multiple
                            owners are supported. The backend rejects
                            deleting the LAST owner (last-owner safety
                            guard), and self-delete is still blocked
                            entirely. */}
                        {!isYou && (
                          <Button variant="danger" size="sm" onClick={() => setDeleteConfirm(u)}>Delete</Button>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
                {!usersListVisible && (
                  <div style={{
                    gridColumn: "1 / -1",
                    padding: "32px 0", textAlign: "center",
                    fontSize: 12, color: COLORS.textDim,
                  }}>
                    User list hidden. Click <strong>Show users</strong> above to reveal.
                  </div>
                )}
              </div>
            </div>

            {/* Permission matrix — shows WHAT each role can do (Batch B6). */}
            <RbacPermissionMatrix />

            {/* Invite Modal */}
            <Modal open={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite User" width={420}>
                  {inviteResult ? (
                    <div>
                      <div style={{
                        padding: "12px 14px", borderRadius: 10, marginBottom: 16,
                        background: inviteResult.success ? `${COLORS.green}10` : `${COLORS.pink}10`,
                        border: `1px solid ${inviteResult.success ? COLORS.green : COLORS.pink}25`,
                        color: inviteResult.success ? COLORS.green : COLORS.pink,
                        fontSize: 13, fontWeight: 500,
                      }}>
                        {inviteResult.message}
                      </div>
                      {inviteResult.link && (
                        <div style={{
                          padding: "10px 12px", borderRadius: 8, background: COLORS.surface,
                          border: `1px solid ${COLORS.border}`, marginBottom: 16,
                          fontSize: 11, color: COLORS.textMuted, wordBreak: "break-all",
                          fontFamily: "monospace", lineHeight: 1.6,
                        }}>
                          {inviteResult.link}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <Button variant="secondary" onClick={() => setInviteResult(null)} style={{ flex: 1 }}>Invite Another</Button>
                        <Button variant="secondary" onClick={() => setShowInviteModal(false)} style={{ flex: 1 }}>Close</Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      <div>
                        <label style={{ display: "block", fontSize: 11, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Email</label>
                        <input type="email" placeholder="colleague@company.com" value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          style={{
                            width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
                            border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
                            fontSize: 13, outline: "none",
                          }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 11, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Name</label>
                        <input type="text" placeholder="John Doe" value={inviteName}
                          onChange={(e) => setInviteName(e.target.value)}
                          style={{
                            width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
                            border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
                            fontSize: 13, outline: "none",
                          }} />
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 11, color: COLORS.textMuted, fontWeight: 500, marginBottom: 6 }}>Role</label>
                        <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                          style={{
                            width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
                            border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
                            fontSize: 13, outline: "none", cursor: "pointer",
                          }}>
                          <option value="business_user">Business User</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="developer">Developer</option>
                          <option value="administrator">Administrator</option>
                          {currentUser?.role === "owner" && <option value="owner">Owner</option>}
                        </select>
                      </div>
                      <button onClick={handleInvite}
                        disabled={inviteLoading || !inviteEmail || !inviteName}
                        style={{
                          width: "100%", padding: "11px 0", borderRadius: 8, border: "none",
                          background: (!inviteEmail || !inviteName) ? COLORS.border : `linear-gradient(135deg, ${COLORS.accent}, #6366F1)`,
                          color: (!inviteEmail || !inviteName) ? COLORS.textDim : "#fff",
                          fontSize: 13, fontWeight: 700, cursor: inviteLoading ? "wait" : "pointer",
                          boxShadow: (!inviteEmail || !inviteName) ? "none" : `0 4px 12px ${COLORS.accentGlow}`,
                          transition: "all 0.2s", marginTop: 4,
                        }}>
                        {inviteLoading ? "Sending..." : "Send Invitation"}
                      </button>
                    </div>
                  )}
            </Modal>

            {/* Edit User Modal */}
            <Modal
              open={!!editingUser}
              onClose={() => setEditingUser(null)}
              title={`Edit User`}
              width={420}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setEditingUser(null)}>Cancel</Button>
                  <Button onClick={handleSaveUser} loading={editLoading}>Save Changes</Button>
                </>
              }
            >
              {editingUser && (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {editError && (
                    <div style={{
                      padding: "10px 12px", borderRadius: 8,
                      background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`,
                      color: COLORS.pink, fontSize: 12, fontWeight: 500,
                    }}>{editError}</div>
                  )}
                  <Input
                    label="Name"
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="User name"
                  />
                  <div>
                    <Label>Email</Label>
                    <div style={{
                      padding: "8px 10px", borderRadius: 7, fontSize: 12,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.textDim,
                    }}>{editingUser.email}</div>
                    <div style={{ fontSize: 9, color: COLORS.textDim, marginTop: 3 }}>Email cannot be changed</div>
                  </div>
                  <Select
                    label="Role"
                    value={editForm.role}
                    onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                    disabled={currentUser && editingUser.id === currentUser.id}
                  >
                    {/* Owners can promote anyone to owner; other admins
                        can only see "Owner" if the user is already one
                        (read-only display, since the backend rejects
                        their attempt to change it). */}
                    {(currentUser?.role === "owner" || editingUser.role === "owner") && (
                      <option value="owner">Owner</option>
                    )}
                    <option value="administrator">Administrator</option>
                    <option value="developer">Developer</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="business_user">Business User</option>
                  </Select>
                  {currentUser && editingUser.id === currentUser.id && (
                    <div style={{ fontSize: 10, color: COLORS.orange }}>You cannot change your own role</div>
                  )}
                  <Select
                    label="Status"
                    value={editForm.status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    {editingUser.status === "invited" && <option value="invited">Invited</option>}
                  </Select>
                </div>
              )}
            </Modal>

            {/* Delete User Confirmation */}
            <Modal
              open={!!deleteConfirm}
              onClose={() => setDeleteConfirm(null)}
              title="Delete User"
              width={400}
              footer={
                <>
                  <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                  <Button variant="danger" onClick={() => handleDeleteUser(deleteConfirm)} loading={deleteLoading}>Delete User</Button>
                </>
              }
            >
              {deleteConfirm && (
                <div>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 12 }}>
                    Are you sure you want to remove <strong>{deleteConfirm.name}</strong> ({deleteConfirm.email}) from this workspace?
                  </div>
                  <div style={{
                    padding: "10px 12px", borderRadius: 8,
                    background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`,
                    color: COLORS.pink, fontSize: 11,
                  }}>
                    This action cannot be undone. The user will lose access immediately and their API keys will be revoked.
                  </div>
                </div>
              )}
            </Modal>
          </div>
        )}

        {/* ===== API KEYS & INTEGRATIONS ===== */}
        {adminTab === "apikeys" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>API Keys & Services</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Manage API keys and view system service status</div>
              </div>
              <Button variant="secondary" onClick={() => { fetchApiKeys(); fetchIntegrations(); }}>Refresh</Button>
            </div>

            {/* API Keys */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "20px 24px", marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>API Keys</div>
                <Button onClick={() => { setCreatedKey(null); setCreatedKeyScopes([]); setNewKeyName("API Key"); setNewKeyScopes(["platform"]); setShowCreateKey(true); }}>+ Create Key</Button>
              </div>
              {apiKeysLoading ? (
                <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "12px 0" }}>Loading...</div>
              ) : apiKeys.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "16px 0", textAlign: "center" }}>
                  No API keys yet. Create one to access platform APIs or embed the chat widget.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 1fr 0.6fr" : "1.5fr 1fr 0.8fr 0.7fr 1fr 0.6fr", gap: 0 }}>
                  {(bp.isMobile ? ["Name", "Key", ""] : ["Name", "Key Prefix", "Scopes", "Calls", "Last Used", ""]).map(h => (
                    <div key={h || "actions"} style={{
                      fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.06em",
                      textTransform: "uppercase", padding: "0 0 12px 0", borderBottom: `1px solid ${COLORS.border}`,
                    }}>{h || "Actions"}</div>
                  ))}
                  {apiKeys.map(k => (
                    <React.Fragment key={k.id}>
                      <div style={{ padding: "14px 0", fontSize: 13, fontWeight: 500, color: COLORS.text, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                        {k.name}
                        <Badge color={k.status === "active" ? COLORS.green : COLORS.textDim} style={{ textTransform: "uppercase", fontSize: 9 }}>{k.status}</Badge>
                      </div>
                      <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                        <code style={{
                          fontSize: 11, color: COLORS.textMuted, background: COLORS.surface,
                          padding: "3px 8px", borderRadius: 5, fontFamily: "monospace",
                          border: `1px solid ${COLORS.border}`,
                        }}>{k.key_prefix}...</code>
                      </div>
                      {!bp.isMobile && (
                        <>
                          <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                            {(k.scopes || []).map(s => (
                              <span key={s} style={{
                                fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                                padding: "2px 6px", borderRadius: 4,
                                background: s === "platform" ? `${COLORS.accent}18` : `${COLORS.cyan}18`,
                                color: s === "platform" ? COLORS.accent : COLORS.cyan,
                                border: `1px solid ${s === "platform" ? COLORS.accent + "30" : COLORS.cyan + "30"}`,
                              }}>{s}</span>
                            ))}
                          </div>
                          <div style={{ padding: "14px 0", fontSize: 12, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                            {(k.call_count || 0).toLocaleString()}
                          </div>
                          <div style={{ padding: "14px 0", fontSize: 11, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                            {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}
                          </div>
                        </>
                      )}
                      <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                        {k.status === "active" && (
                          <Button size="small" variant="secondary" onClick={() => setRevokeConfirm(k)}>Revoke</Button>
                        )}
                        <Button size="small" variant="danger" onClick={() => setDeleteConfirmKey(k)}>Delete</Button>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            {/* Branding — per-tenant logo (Admin/Owner only) */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: bp.isMobile ? "16px 12px" : "20px 24px", marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Branding</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16 }}>
                Replace the default I² logo in the topbar with your own. PNG, JPEG, WebP or SVG — up to 2MB.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <div style={{
                  width: 64, height: 64, borderRadius: RADIUS.inner, border: `1px solid ${COLORS.border}`,
                  background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Logo size={40} hasCustomLogo={!!currentTenant?.has_custom_logo} />
                </div>
                {isAdmin ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <label style={{
                        display: "inline-flex", alignItems: "center", padding: "8px 14px", borderRadius: 8,
                        fontSize: 12, fontWeight: 600, cursor: logoUploading ? "default" : "pointer",
                        color: COLORS.accent, background: `${COLORS.accent}10`, border: `1px solid ${COLORS.accent}30`,
                        opacity: logoUploading ? 0.6 : 1,
                      }}>
                        {logoUploading ? "Uploading…" : currentTenant?.has_custom_logo ? "Replace Logo" : "Upload Logo"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={handleLogoFileChange}
                          disabled={logoUploading}
                          style={{ display: "none" }}
                        />
                      </label>
                      {currentTenant?.has_custom_logo && (
                        <Button size="small" variant="secondary" disabled={logoUploading} onClick={handleRemoveLogo}>
                          Remove Logo
                        </Button>
                      )}
                    </div>
                    {logoError && <div style={{ fontSize: 11, color: COLORS.pink }}>{logoError}</div>}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: COLORS.textDim }}>Only owners and administrators can change branding.</div>
                )}
              </div>
            </div>

            {/* Integration Details */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: bp.isMobile ? "16px 12px" : "20px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>Integration Details</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 16 }}>Configure API keys and credentials for platform services</div>
              <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
                {integrations.map(intg => {
                  const editable = !!INTG_CONFIG[intg.name];
                  const colorMap = { "OpenAI": "#10A37F", "Anthropic": "#D97706", "Google Vertex AI": "#4285F4", "AWS Bedrock": "#FF9900", "WhatsApp": "#25D366", "Twilio": COLORS.pink, "Deepgram": COLORS.cyan, "ElevenLabs": COLORS.accent, "Genesys Cloud": "#FF4F1F", "OpenObserve": "#F59E0B", "Platform": COLORS.cyan };
                  const descMap = { "OpenAI": "GPT models for AI chat", "Anthropic": "Claude LLM provider", "Google Vertex AI": "Gemini LLM provider", "AWS Bedrock": "Bedrock LLM provider (Claude, Titan, Llama)", "WhatsApp": "WhatsApp Business messaging", "Twilio": "SMS channel provider", "Deepgram": "Speech-to-text for voice", "ElevenLabs": "Text-to-speech for voice", "Genesys Cloud": "Contact center for human agent handoff", "OpenObserve": "Logs, traces & dashboards", "Platform": "Cross-cutting platform settings (webhook base URL, etc.)" };
                  const color = colorMap[intg.name] || COLORS.accent;
                  const desc = descMap[intg.name] || intg.category;
                  const testFailed = intgTestFailed[intg.name] === true;
                  const healthy = intg.configured && !testFailed;
                  return (
                    <div key={intg.name} style={{
                      padding: "16px 18px", borderRadius: RADIUS.inner,
                      background: COLORS.surface,
                      border: `1px solid ${healthy ? COLORS.green + "25" : testFailed ? COLORS.pink + "25" : COLORS.border}`,
                      boxShadow: MATERIALS.spatialShadow(),
                      cursor: editable ? "pointer" : "default", transition: "border-color 0.15s",
                    }} onClick={() => editable && handleEditIntegration(intg)}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: `${color}15`, border: `1px solid ${color}30`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 16, color: color, fontWeight: 700,
                          }}>{intg.icon}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{intg.name}</div>
                        </div>
                        {intg.configured && <div style={{
                          width: 8, height: 8, borderRadius: "50%",
                          background: testFailed ? COLORS.pink : COLORS.green,
                          boxShadow: `0 0 6px ${testFailed ? COLORS.pink : COLORS.green}66`,
                        }} />}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>{desc}</div>
                      {testFailed ? (
                        <div style={{
                          width: "100%", padding: "6px 0", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          textAlign: "center", color: COLORS.pink, background: `${COLORS.pink}08`,
                          border: `1px solid ${COLORS.pink}20`,
                        }}>Connection Failed</div>
                      ) : intg.configured ? (
                        <div style={{
                          width: "100%", padding: "6px 0", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          textAlign: "center", color: COLORS.green, background: `${COLORS.green}08`,
                          border: `1px solid ${COLORS.green}20`,
                        }}>Configured</div>
                      ) : editable ? (
                        <div style={{
                          width: "100%", padding: "6px 0", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          textAlign: "center", color: color, background: `${color}10`,
                          border: `1px solid ${color}30`,
                        }}>Configure</div>
                      ) : (
                        <div style={{
                          width: "100%", padding: "6px 0", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          textAlign: "center", color: COLORS.textDim, background: `${COLORS.textDim}08`,
                          border: `1px solid ${COLORS.border}`,
                        }}>Not Set</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Integration Edit Modal */}
            {editingIntegration && INTG_CONFIG[editingIntegration.name] && (
              <Modal open={true} onClose={() => { setEditingIntegration(null); setOoCreds(null); setOoCredsError(null); }} title={INTG_CONFIG[editingIntegration.name].readOnly ? editingIntegration.name : `Configure ${editingIntegration.name}`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {INTG_CONFIG[editingIntegration.name].readOnly ? (
                    <>
                      {intgForm._org && (
                        <div>
                          <Label>Organization</Label>
                          <div style={{ fontSize: 13, color: COLORS.text, padding: "8px 12px", background: COLORS.surface, borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, fontFamily: "monospace" }}>{intgForm._org}</div>
                        </div>
                      )}
                      {intgForm._dashboard_url && (
                        <div>
                          <Label>Dashboard URL</Label>
                          <a
                            href={intgForm._dashboard_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "flex", alignItems: "center", gap: 8,
                              padding: "12px 16px", borderRadius: 10,
                              background: `#F59E0B12`, border: `1px solid #F59E0B30`,
                              color: "#F59E0B", fontSize: 13, fontWeight: 600,
                              textDecoration: "none", transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#F59E0B20"}
                            onMouseLeave={e => e.currentTarget.style.background = "#F59E0B12"}
                          >
                            <span style={{ fontSize: 18 }}>📊</span>
                            Open Dashboards
                            <span style={{ marginLeft: "auto", fontSize: 14 }}>↗</span>
                          </a>
                        </div>
                      )}
                      {!intgForm._enabled && (
                        <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12, color: COLORS.textMuted, background: `${COLORS.textDim}08`, border: `1px solid ${COLORS.border}` }}>
                          Tenant isolation is not enabled. Enable <code style={{ fontSize: 11 }}>openobserve_tenant_isolation</code> to provision per-tenant dashboards.
                        </div>
                      )}

                      {/* Credentials section — only meaningful when the
                          OpenObserve widget is for an actual integration.
                          `_password_set` comes from GET /openobserve-config
                          (boolean, not the password itself). The password
                          is fetched via GET /openobserve-config/credentials
                          only when the admin clicks "Show Credentials". */}
                      {editingIntegration.name === "OpenObserve" && intgForm._enabled && (
                        <div style={{ padding: "14px 16px", borderRadius: 10, background: `${COLORS.bg}`, border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
                          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>Login Credentials</div>
                          {!intgForm._password_set ? (
                            <>
                              <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>
                                No per-tenant OpenObserve user has been provisioned yet for this tenant. Provisioning creates a dedicated org in OpenObserve and a user with dashboard access. You only need to do this once.
                              </div>
                              <Button
                                size="sm"
                                disabled={ooProvisioning}
                                onClick={async () => {
                                  setOoProvisioning(true);
                                  setOoCredsError(null);
                                  try {
                                    const r = await authApi.provisionOpenObserve();
                                    if (r.success) {
                                      // Reload the config to flip _password_set to true
                                      const cfg = await authApi.getOpenObserveConfig();
                                      const d = cfg.data || {};
                                      setIntgForm(prev => ({
                                        ...prev,
                                        _password_set: !!d.password_set,
                                        _email: d.user_email || prev._email,
                                        _org: d.org || prev._org,
                                      }));
                                    } else {
                                      setOoCredsError(r.message || "Provision failed");
                                    }
                                  } catch (err) {
                                    setOoCredsError(err.message || String(err));
                                  } finally {
                                    setOoProvisioning(false);
                                  }
                                }}
                                style={{ background: "#F59E0B", color: "#fff", fontWeight: 600 }}
                              >
                                {ooProvisioning ? "Provisioning…" : "Provision OpenObserve user"}
                              </Button>
                            </>
                          ) : (
                            <>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ fontSize: 11, color: COLORS.textMuted }}>Email</div>
                                <div style={{ fontSize: 13, color: COLORS.text, fontFamily: "monospace", padding: "6px 10px", background: COLORS.surface, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
                                  {intgForm._email || "(not available)"}
                                </div>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ fontSize: 11, color: COLORS.textMuted }}>Password</div>
                                {ooCreds?.password ? (
                                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                    <div style={{ flex: 1, fontSize: 13, color: COLORS.text, fontFamily: "monospace", padding: "6px 10px", background: COLORS.surface, borderRadius: 6, border: `1px solid ${COLORS.border}` }}>
                                      {ooCreds.password}
                                    </div>
                                    <Button size="sm" onClick={() => { navigator.clipboard?.writeText(ooCreds.password); }} style={{ background: `${COLORS.accent}15`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30` }}>Copy</Button>
                                    <Button size="sm" onClick={() => setOoCreds(null)} style={{ background: `${COLORS.textDim}10`, color: COLORS.textMuted, border: `1px solid ${COLORS.border}` }}>Hide</Button>
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    disabled={ooCredsLoading}
                                    onClick={async () => {
                                      setOoCredsLoading(true);
                                      setOoCredsError(null);
                                      try {
                                        const r = await authApi.getOpenObserveCredentials();
                                        if (r.success && r.data) {
                                          setOoCreds(r.data);
                                        } else {
                                          setOoCredsError(r.message || "Credentials not available");
                                        }
                                      } catch (err) {
                                        setOoCredsError(err.message || String(err));
                                      } finally {
                                        setOoCredsLoading(false);
                                      }
                                    }}
                                    style={{ background: "#F59E0B12", color: "#F59E0B", border: "1px solid #F59E0B30", fontWeight: 600 }}
                                  >
                                    {ooCredsLoading ? "Loading…" : "Show Credentials"}
                                  </Button>
                                )}
                              </div>
                              {intgForm._login_url && (
                                <a
                                  href={intgForm._login_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 11, color: COLORS.accentText, textDecoration: "underline", marginTop: 4 }}
                                >
                                  Open OpenObserve login ↗
                                </a>
                              )}
                            </>
                          )}
                          {ooCredsError && (
                            <div style={{ fontSize: 11, color: COLORS.pink, background: `${COLORS.pink}08`, border: `1px solid ${COLORS.pink}20`, padding: "6px 10px", borderRadius: 6 }}>
                              {ooCredsError}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {INTG_CONFIG[editingIntegration.name].notice && (
                        // Compliance / DPA notice block — rendered above the
                        // fields when an integration's config block declares
                        // a `notice` string. Currently used by Deepseek (China
                        // data routing); pattern is reusable for any
                        // jurisdiction-sensitive provider added later.
                        <div style={{
                          padding: "10px 12px", borderRadius: 8, marginBottom: 14,
                          background: `${COLORS.orange}10`,
                          border: `1px solid ${COLORS.orange}30`,
                          fontSize: 11, color: COLORS.text, lineHeight: 1.45,
                        }}>
                          <span style={{ fontWeight: 700, color: COLORS.orange, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 9 }}>
                            Compliance notice
                          </span>
                          <div style={{ marginTop: 4 }}>
                            {INTG_CONFIG[editingIntegration.name].notice}
                          </div>
                        </div>
                      )}
                      {INTG_CONFIG[editingIntegration.name].fields.map(f => (
                        <div key={f.key}>
                          {f.type === "custom" && f.key === "_voice_pool" ? (
                            <div>
                              <Label>Voice Pool</Label>
                              <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 8 }}>
                                Add multiple voices. Set the "Active Voice ID" above to choose which one is used.
                              </div>
                              {/* Voice pool grid */}
                              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                                {(intgForm.elevenlabs_voice_pool || []).map((v, i) => (
                                  <div key={i} style={{
                                    display: "flex", gap: 8, alignItems: "center",
                                    padding: "8px 12px", borderRadius: 8,
                                    background: v.voice_id === intgForm.elevenlabs_voice_id ? `${COLORS.accent}10` : COLORS.bg,
                                    border: `1px solid ${v.voice_id === intgForm.elevenlabs_voice_id ? COLORS.accent + "40" : COLORS.border}`,
                                  }}>
                                    <span style={{ fontSize: 14, width: 20, textAlign: "center" }}>{v.voice_id === intgForm.elevenlabs_voice_id ? "\uD83D\uDD0A" : "\uD83C\uDFA4"}</span>
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{v.name || "Unnamed"}</div>
                                      <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "monospace" }}>{v.voice_id}</div>
                                    </div>
                                    <button onClick={() => setIntgForm(prev => ({ ...prev, elevenlabs_voice_id: v.voice_id }))} title="Set as active" style={{
                                      padding: "3px 8px", borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: "pointer",
                                      background: v.voice_id === intgForm.elevenlabs_voice_id ? COLORS.accent : "transparent",
                                      border: `1px solid ${v.voice_id === intgForm.elevenlabs_voice_id ? COLORS.accent : COLORS.border}`,
                                      color: v.voice_id === intgForm.elevenlabs_voice_id ? "#fff" : COLORS.textMuted,
                                    }}>{v.voice_id === intgForm.elevenlabs_voice_id ? "Active" : "Use"}</button>
                                    <button onClick={() => setIntgForm(prev => ({
                                      ...prev,
                                      elevenlabs_voice_pool: (prev.elevenlabs_voice_pool || []).filter((_, idx) => idx !== i),
                                    }))} title="Remove" style={{
                                      width: 24, height: 24, borderRadius: 4, border: `1px solid ${COLORS.border}`,
                                      background: "transparent", color: COLORS.textMuted, cursor: "pointer", fontSize: 12,
                                      display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                                    }}>{"\u00D7"}</button>
                                  </div>
                                ))}
                                {(intgForm.elevenlabs_voice_pool || []).length === 0 && (
                                  <div style={{ fontSize: 11, color: COLORS.textDim, padding: "12px 0", textAlign: "center" }}>
                                    No voices in pool. Add one below.
                                  </div>
                                )}
                              </div>
                              {/* Add voice form */}
                              <div style={{ display: "flex", gap: 6 }}>
                                <Input placeholder="Voice name (e.g. Sarah)" value={intgForm._newVoiceName || ""}
                                  onChange={e => setIntgForm(prev => ({ ...prev, _newVoiceName: e.target.value }))}
                                  style={{ flex: 1, fontSize: 11, padding: "6px 10px" }} />
                                <Input placeholder="Voice ID" value={intgForm._newVoiceId || ""}
                                  onChange={e => setIntgForm(prev => ({ ...prev, _newVoiceId: e.target.value }))}
                                  style={{ flex: 1, fontSize: 11, padding: "6px 10px", fontFamily: "monospace" }} />
                                <button onClick={() => {
                                  if (!intgForm._newVoiceId?.trim()) return;
                                  setIntgForm(prev => ({
                                    ...prev,
                                    elevenlabs_voice_pool: [...(prev.elevenlabs_voice_pool || []), { name: prev._newVoiceName?.trim() || "Unnamed", voice_id: prev._newVoiceId.trim() }],
                                    _newVoiceName: "", _newVoiceId: "",
                                  }));
                                }} style={{
                                  padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                                  background: COLORS.accent, color: "#fff", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                                }}>Add</button>
                              </div>
                            </div>
                          ) : f.type === "checkbox" ? (
                            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: COLORS.text }}>
                              <input
                                type="checkbox"
                                checked={!!intgForm[f.key]}
                                onChange={e => setIntgForm(prev => ({ ...prev, [f.key]: e.target.checked }))}
                                style={{ width: 16, height: 16, accentColor: COLORS.accent }}
                              />
                              {f.label}
                            </label>
                          ) : (
                            <>
                              <Label>
                                {f.label}
                                {f.type === "password" && intgForm._existing_masks?.[f.key] && (
                                  <span style={{ marginLeft: 8, padding: "1px 6px", fontSize: 9, fontWeight: 600, borderRadius: 4, background: `${COLORS.green}20`, color: COLORS.green, border: `1px solid ${COLORS.green}40`, letterSpacing: "0.04em" }}>
                                    ✓ CONFIGURED
                                  </span>
                                )}
                              </Label>
                              <Input
                                type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
                                step={f.step}
                                placeholder={
                                  f.type === "password" && intgForm._existing_masks?.[f.key]
                                    ? `Currently set (${intgForm._existing_masks[f.key]}) — type new value to replace`
                                    : f.placeholder
                                }
                                value={intgForm[f.key] ?? ""}
                                onChange={e => setIntgForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                                style={{ width: "100%", fontFamily: f.type === "password" ? "monospace" : "inherit" }}
                                data-field={f.key}
                              />
                              {f.type === "password" && (
                                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
                                  {intgForm._existing_masks?.[f.key]
                                    ? "Leave blank to keep the existing value; type a new value to replace it."
                                    : "No value currently stored."}
                                </div>
                              )}
                              {f.helper && (
                                <div style={{ fontSize: 10.5, color: COLORS.textDim, marginTop: 4, lineHeight: 1.45 }}>{f.helper}</div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {intgTestResult && (
                    <div style={{
                      padding: "10px 14px", borderRadius: 8, fontSize: 12,
                      background: intgTestResult.success ? `${COLORS.green}15` : `${COLORS.red}15`,
                      color: intgTestResult.success ? COLORS.green : COLORS.red,
                      border: `1px solid ${intgTestResult.success ? COLORS.green : COLORS.red}30`,
                    }}>{intgTestResult.message}</div>
                  )}

                  {intgError && (
                    <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12, background: `${COLORS.red}15`, color: COLORS.red, border: `1px solid ${COLORS.red}30` }}>{intgError}</div>
                  )}

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                    {INTG_CONFIG[editingIntegration.name].clear && editingIntegration.configured && (
                      <Button variant="ghost" onClick={handleClearIntegration} disabled={intgSaving}
                        style={{ marginRight: "auto", color: COLORS.red }}>
                        {INTG_CONFIG[editingIntegration.name].clear.label}
                      </Button>
                    )}
                    {INTG_CONFIG[editingIntegration.name].test && (
                      <Button variant="ghost" onClick={handleTestIntegration} disabled={intgTesting}>
                        {intgTesting ? "Testing..." : "Test Connection"}
                      </Button>
                    )}
                    {!INTG_CONFIG[editingIntegration.name].readOnly && (
                      <Button onClick={handleSaveIntegration} disabled={intgSaving}>
                        {intgSaving ? "Saving..." : "Save"}
                      </Button>
                    )}
                  </div>
                </div>
              </Modal>
            )}

          </div>
        )}

        {/* ===== AUDIT LOGS ===== */}
        {adminTab === "logs" && (() => {
          const ACTION_META = {
            "user.login": { icon: "\uD83D\uDD11", label: "User logged in", category: "Auth" },
            "user.login_failed": { icon: "\u274C", label: "Login failed", category: "Auth" },
            "user.invited": { icon: "\uD83D\uDCE8", label: "User invited", category: "Auth" },
            "user.invite_accepted": { icon: "\u2705", label: "Invite accepted", category: "Auth" },
            "user.updated": { icon: "\u270F\uFE0F", label: "User updated", category: "Auth" },
            "user.deleted": { icon: "\uD83D\uDDD1\uFE0F", label: "User deleted", category: "Auth" },
            "user.password_reset": { icon: "\uD83D\uDD12", label: "Password reset", category: "Auth" },
            "user.password_reset_requested": { icon: "\uD83D\uDD12", label: "Password reset requested", category: "Auth" },
            "tenant.registered": { icon: "\uD83C\uDFE2", label: "Workspace registered", category: "Auth" },
            "agent.created": { icon: "\uD83E\uDD16", label: "Agent created", category: "Agents" },
            "agent.updated": { icon: "\u270F\uFE0F", label: "Agent updated", category: "Agents" },
            "agent.deleted": { icon: "\uD83D\uDDD1\uFE0F", label: "Agent deleted", category: "Agents" },
            "agent.status_changed": { icon: "\uD83D\uDD04", label: "Agent status changed", category: "Agents" },
            "agent.few_shots_applied": { icon: "\uD83E\uDDE0", label: "Few-shots applied", category: "Agents" },
            "agent.suggestion_applied": { icon: "\uD83D\uDCA1", label: "Prompt suggestion applied", category: "Agents" },
            "ab_test.created": { icon: "\u2696", label: "A/B test created", category: "Agents" },
            "ab_test.started": { icon: "\u25B6", label: "A/B test started", category: "Agents" },
            "campaign.created": { icon: "\uD83D\uDCE2", label: "Campaign created", category: "Campaigns" },
            "campaign.triggered": { icon: "\uD83D\uDE80", label: "Campaign triggered", category: "Campaigns" },
            "campaign.deleted": { icon: "\uD83D\uDDD1\uFE0F", label: "Campaign deleted", category: "Campaigns" },
            // D8 \u2014 Voice outbound v3 audit categories. Filtered via the
            // "Voice" filter chip below. Backend writes use the
            // ``voice.*`` action prefix consistently \u2014 keep this list in
            // sync with audit_repo.log() call sites.
            // B56 (2026-07-23): the browser voice-call lifecycle actions
            // (voice.py) were absent from this map, so they fell through
            // getMeta's default \u2192 category "System" and never showed
            // under the "Voice" filter, despite being audited correctly
            // backend-side. The prefix fallback in getMeta now catches
            // any future voice.* too; these entries add proper labels.
            "voice.session_started": { icon: "\ud83d\udcde", label: "Voice session started", category: "Voice" },
            "voice.session_ended": { icon: "\ud83d\udcf4", label: "Voice session ended", category: "Voice" },
            "voice.room_deleted": { icon: "\ud83d\uddd1\ufe0f", label: "Voice room deleted", category: "Voice" },
            "voice.session_transferred": { icon: "\u21aa\ufe0f", label: "Voice session transferred", category: "Voice" },
            "voice.config_updated": { icon: "\u270f\ufe0f", label: "Voice config updated", category: "Voice" },
            "voice.attestation_recorded": { icon: "\u2705", label: "Tenant attestation recorded", category: "Voice" },
            "voice.campaign_launched": { icon: "\uD83D\uDCDE", label: "Voice campaign launched", category: "Voice" },
            "voice.campaign_paused": { icon: "\u23F8", label: "Voice campaign paused", category: "Voice" },
            "voice.campaign_resumed": { icon: "\u25B6", label: "Voice campaign resumed", category: "Voice" },
            "voice.campaign_stopped": { icon: "\u23F9", label: "Voice campaign stopped", category: "Voice" },
            "voice.call_dispatched": { icon: "\uD83D\uDCDE", label: "Call dispatched", category: "Voice" },
            "voice.call_completed": { icon: "\u2714", label: "Call completed", category: "Voice" },
            "voice.trunk_created": { icon: "\uD83D\uDD17", label: "Voice trunk created", category: "Voice" },
            "voice.trunk_updated": { icon: "\u270F\uFE0F", label: "Voice trunk updated", category: "Voice" },
            "voice.trunk_test_run": { icon: "\uD83E\uDDEA", label: "Voice trunk test run", category: "Voice" },
            "voice.trunk_deleted": { icon: "\uD83D\uDDD1\uFE0F", label: "Voice trunk deleted", category: "Voice" },
            "voice.recording_accessed": { icon: "\uD83D\uDD09", label: "Call recording accessed", category: "Voice" },
            "voice.breaker_tripped": { icon: "\u26A0", label: "Trunk circuit breaker tripped", category: "Voice" },
            "whatsapp.config_updated": { icon: "\uD83D\uDCAC", label: "WhatsApp config updated", category: "Config" },
            "sms.config_updated": { icon: "\uD83D\uDCF1", label: "SMS config updated", category: "Config" },
            "smtp.config_updated": { icon: "\u2709", label: "SMTP config updated", category: "Config" },
            "llm.config_updated": { icon: "\uD83E\uDDE0", label: "LLM config updated", category: "Config" },
            "llm.rankings_updated": { icon: "\uD83C\uDFC6", label: "LLM rankings updated", category: "Config" },
            "billing.plan_updated": { icon: "\uD83D\uDCB3", label: "Plan updated", category: "Billing" },
            "billing.quotas_updated": { icon: "\uD83D\uDCB3", label: "Quotas updated", category: "Billing" },
            "compliance.retention_applied": { icon: "\uD83D\uDEE1", label: "Retention policy applied", category: "Compliance" },
            "compliance.dsar_generated": { icon: "\uD83D\uDEE1", label: "DSAR report generated", category: "Compliance" },
            "ingestion.upload_started": { icon: "\uD83D\uDCC4", label: "Document ingestion started", category: "System" },
            "mcp.server_registered": { icon: "\uD83D\uDD0C", label: "MCP server registered", category: "System" },
            "mcp.server_unregistered": { icon: "\u26D4", label: "MCP server removed", category: "System" },
            "openobserve.provisioned": { icon: "\uD83D\uDCCA", label: "OpenObserve provisioned", category: "System" },
          };
          // B56: prefix \u2192 category fallback. An action not in ACTION_META
          // used to default to "System", so it vanished from its real
          // channel/domain filter (the voice-lifecycle bug). Derive the
          // category from the action prefix so a new backend action shows
          // under the right filter WITHOUT a map edit; ACTION_META still
          // wins for icons/labels/overrides.
          const PREFIX_CATEGORY = {
            voice: "Voice", agent: "Agents", conversation: "Agents",
            auth: "Auth", user: "Auth", tenant: "Config",
            whatsapp: "Config", sms: "Config", smtp: "Config", llm: "Config",
            campaign: "Campaigns", schedule: "Campaigns",
            compliance: "Compliance", audit: "Compliance", dsar: "Compliance",
            billing: "Billing", quota: "Billing", plan: "Billing",
          };
          const getMeta = (action) => {
            if (ACTION_META[action]) return ACTION_META[action];
            const prefix = String(action || "").split(".")[0];
            return {
              icon: "\uD83D\uDCCB",
              label: action.replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase()),
              category: PREFIX_CATEGORY[prefix] || "System",
            };
          };
          const severityColor = { info: COLORS.cyan, warning: COLORS.orange, error: COLORS.pink };
          const filters = ["All", "Auth", "Agents", "Config", "Campaigns", "Voice", "Compliance", "Billing", "System"];
          const searchLower = (logSearch || "").toLowerCase();
          const filtered = auditLogs
            .filter(l => logFilter === "All" || getMeta(l.action).category === logFilter)
            .filter(l => !searchLower || l.action.toLowerCase().includes(searchLower)
              || (l.details && JSON.stringify(l.details).toLowerCase().includes(searchLower))
              || getMeta(l.action).label.toLowerCase().includes(searchLower));
          const infoCount = auditLogs.filter(l => l.severity === "info").length;
          const warnCount = auditLogs.filter(l => l.severity === "warning").length;
          const errorCount = auditLogs.filter(l => l.severity === "error").length;

          const timeAgo = (iso) => {
            if (!iso) return "\u2014";
            const diff = (Date.now() - new Date(iso).getTime()) / 1000;
            if (diff < 60) return "just now";
            if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
            return `${Math.floor(diff / 86400)}d ago`;
          };

          const getDetail = (log) => {
            if (!log.details) return null;
            const parts = [];
            if (log.details.email) parts.push(log.details.email);
            if (log.details.name) parts.push(log.details.name);
            if (log.details.status) parts.push(`Status: ${log.details.status}`);
            if (log.details.channel) parts.push(`Channel: ${log.details.channel}`);
            if (log.details.plan) parts.push(`Plan: ${log.details.plan}`);
            if (log.details.source) parts.push(`Source: ${log.details.source}`);
            if (log.details.count) parts.push(`Count: ${log.details.count}`);
            return parts.length > 0 ? parts.join(" \u00B7 ") : null;
          };

          return (
          <div>
            <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 16, flexDirection: bp.isMobile ? "column" : "row", gap: bp.isMobile ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>Audit Logs & Activity</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Track all actions across the platform</div>
              </div>
              <Button onClick={() => { complianceApi.exportLogs("csv").then(res => {
                const blob = new Blob([typeof res === "string" ? res : JSON.stringify(res.data, null, 2)], { type: "text/csv" });
                const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "audit_logs.csv"; a.click();
              }).catch(() => {}); }} variant="secondary" size="sm">Export CSV</Button>
            </div>

            {/* Search + filters */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <input type="text" value={logSearch || ""} onChange={e => setLogSearch(e.target.value)}
                placeholder="Search actions, details..."
                style={{ flex: 1, minWidth: 180, padding: "8px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12, outline: "none" }}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border} />
              {filters.map(f => (
                <button key={f} onClick={() => setLogFilter(f)} style={{
                  padding: "5px 10px", borderRadius: 6,
                  border: `1px solid ${logFilter === f ? COLORS.accent : COLORS.border}`,
                  background: logFilter === f ? COLORS.accentSoft : "transparent",
                  color: logFilter === f ? COLORS.accent : COLORS.textMuted,
                  fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                }}>{f}</button>
              ))}
            </div>

            {/* Severity summary */}
            <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
              {[
                { label: "Total", count: auditLogs.length, iconName: "block.file", color: COLORS.cyan },
                { label: "Info", count: infoCount, iconName: "status.ok", color: COLORS.green },
                { label: "Warnings", count: warnCount, iconName: "status.warning", color: COLORS.orange },
                { label: "Errors", count: errorCount, iconName: "status.error", color: COLORS.pink },
              ].map(s => (
                <div key={s.label} style={{
                  background: COLORS.surface, border: `1px solid ${s.count > 0 && s.label === "Errors" ? COLORS.pink + "30" : COLORS.border}`,
                  borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
                }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: 8,
                    background: `${s.color}12`, border: `1px solid ${s.color}25`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name={s.iconName} size={16} color={s.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.count}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Log entries */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: bp.isMobile ? "14px 10px" : "18px 22px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
                  Activity {filtered.length !== auditLogs.length && <span style={{ fontWeight: 400, color: COLORS.textDim }}>({filtered.length} of {auditLogs.length})</span>}
                </div>
              </div>
              {filtered.length === 0 ? (
                <div style={{ padding: "32px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{"\uD83D\uDCCB"}</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>{searchLower ? "No matching events" : "No activity yet"}</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {filtered.map(log => {
                    const meta = getMeta(log.action);
                    const detail = getDetail(log);
                    const sColor = severityColor[log.severity] || COLORS.cyan;
                    const isExpanded = expandedLogId === log.id;
                    return (
                      <div key={log.id} onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                        style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                            background: `${sColor}10`, border: `1px solid ${sColor}20`,
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
                          }}>{meta.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{meta.label}</div>
                            {detail && <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail}</div>}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            <Badge color={sColor}>{log.severity}</Badge>
                            <span style={{ fontSize: 10, color: COLORS.textDim, whiteSpace: "nowrap" }}>{timeAgo(log.created_at)}</span>
                          </div>
                        </div>
                        {/* Expanded details */}
                        {isExpanded && (
                          <div onClick={e => e.stopPropagation()} style={{
                            marginTop: 8, marginLeft: 40, padding: 10, borderRadius: 8,
                            background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 11,
                          }}>
                            <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "4px 10px", color: COLORS.textMuted }}>
                              <span style={{ fontWeight: 600 }}>Action</span><span style={{ color: COLORS.text, fontFamily: "monospace" }}>{log.action}</span>
                              {log.resource_type && <><span style={{ fontWeight: 600 }}>Resource</span><span>{log.resource_type}{log.resource_id ? ` / ${log.resource_id.slice(0, 12)}...` : ""}</span></>}
                              {log.user_id && <><span style={{ fontWeight: 600 }}>User</span><span style={{ fontFamily: "monospace" }}>{log.user_id.slice(0, 12)}...</span></>}
                              <span style={{ fontWeight: 600 }}>Time</span><span>{log.created_at ? new Date(log.created_at).toLocaleString() : "—"}</span>
                            </div>
                            {log.details && Object.keys(log.details).length > 0 && (
                              <details style={{ marginTop: 6 }}>
                                <summary style={{ fontSize: 10, color: COLORS.textDim, cursor: "pointer" }}>Raw details</summary>
                                <pre style={{ marginTop: 4, fontSize: 10, color: COLORS.textMuted, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>{JSON.stringify(log.details, null, 2)}</pre>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {/* Create API Key Modal */}
        <Modal
          open={showCreateKey}
          onClose={() => { setShowCreateKey(false); setCreatedKey(null); }}
          title={createdKey ? "API Key Created" : "Create API Key"}
        >
          {createdKey ? (
            <div>
              <div style={{
                padding: "12px 14px", borderRadius: 8,
                background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}25`,
                color: COLORS.green, fontSize: 12, marginBottom: 12,
              }}>
                Copy this key now — it will not be shown again.
              </div>
              <div style={{ position: "relative" }}>
                <code style={{
                  display: "block", padding: "12px 14px", borderRadius: 8,
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                  fontSize: 12, fontFamily: "monospace", color: COLORS.text,
                  wordBreak: "break-all", userSelect: "all",
                }}>{createdKey}</code>
              </div>
              {createdKeyScopes.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>Scopes:</span>
                  {createdKeyScopes.map(s => (
                    <span key={s} style={{
                      fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                      padding: "2px 6px", borderRadius: 4,
                      background: s === "platform" ? `${COLORS.accent}18` : `${COLORS.cyan}18`,
                      color: s === "platform" ? COLORS.accent : COLORS.cyan,
                    }}>{s}</span>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Button onClick={() => { navigator.clipboard.writeText(createdKey); }}>Copy to Clipboard</Button>
              </div>
            </div>
          ) : (
            <div>
              <Label>Key Name</Label>
              <Input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="e.g. Production API Key" />
              <Label style={{ marginTop: 12 }}>Scopes</Label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[
                  { value: "platform", label: "Platform API", desc: "Full access to agents, chat, and management endpoints" },
                  { value: "widget", label: "Widget", desc: "Embed chat widget on external websites" },
                ].map(scope => {
                  const selected = newKeyScopes.includes(scope.value);
                  return (
                    <button
                      key={scope.value}
                      type="button"
                      onClick={() => {
                        setNewKeyScopes(prev =>
                          selected ? prev.filter(s => s !== scope.value) : [...prev, scope.value]
                        );
                      }}
                      style={{
                        padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                        border: `1px solid ${selected ? COLORS.accent : COLORS.border}`,
                        background: selected ? `${COLORS.accent}15` : COLORS.bg,
                        color: selected ? COLORS.accent : COLORS.textMuted,
                        fontSize: 12, fontWeight: 500,
                        transition: "all 0.15s ease",
                      }}
                    >
                      {scope.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 6 }}>
                {newKeyScopes.length === 0 ? "Select at least one scope" : newKeyScopes.map(s => {
                  const labels = { platform: "Platform API access", widget: "Widget embedding" };
                  return labels[s] || s;
                }).join(" + ")}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <Button variant="secondary" onClick={() => setShowCreateKey(false)}>Cancel</Button>
                <Button onClick={handleCreateKey} disabled={createKeyLoading || !newKeyName.trim() || newKeyScopes.length === 0}>
                  {createKeyLoading ? "Creating..." : "Create Key"}
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Revoke API Key Confirm */}
        <Modal
          open={!!revokeConfirm}
          onClose={() => setRevokeConfirm(null)}
          title="Revoke API Key"
          footer={
            <>
              <Button variant="secondary" onClick={() => setRevokeConfirm(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => handleRevokeKey(revokeConfirm?.id)} disabled={revokeLoading}>
                {revokeLoading ? "Revoking..." : "Revoke Key"}
              </Button>
            </>
          }
        >
          {revokeConfirm && (
            <div>
              <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 12 }}>
                Are you sure you want to revoke <strong>{revokeConfirm.name}</strong> ({revokeConfirm.key_prefix}...)?
              </div>
              <div style={{
                padding: "10px 12px", borderRadius: 8,
                background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`,
                color: COLORS.pink, fontSize: 11,
              }}>
                Any applications or integrations using this key will immediately stop working.
              </div>
            </div>
          )}
        </Modal>

        {/* Delete API Key Confirm */}
        <Modal
          open={!!deleteConfirmKey}
          onClose={() => setDeleteConfirmKey(null)}
          title="Delete API Key"
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeleteConfirmKey(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => handleDeleteKey(deleteConfirmKey?.id)} disabled={deleteKeyLoading}>
                {deleteKeyLoading ? "Deleting..." : "Delete Key"}
              </Button>
            </>
          }
        >
          {deleteConfirmKey && (
            <div>
              <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 12 }}>
                Permanently delete <strong>{deleteConfirmKey.name}</strong> ({deleteConfirmKey.key_prefix}...)?
              </div>
              <div style={{
                padding: "10px 12px", borderRadius: 8,
                background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25`,
                color: COLORS.pink, fontSize: 11,
              }}>
                This action cannot be undone. The key will be permanently removed.
              </div>
            </div>
          )}
        </Modal>

        {/* ===== BILLING & USAGE ===== */}
        {adminTab === "billing" && <BillingTab />}
        {adminTab === "openapi" && isAdmin && <OpenApiIntegrationsTab />}
        {/* ===== MCP SERVERS ===== */}
        {adminTab === "mcp" && (
          <div>
            <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: 24, flexDirection: bp.isMobile ? "column" : "row", gap: bp.isMobile ? 12 : 0 }}>
              <div>
                <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>MCP Servers</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>Register and manage Model Context Protocol tool servers</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" onClick={() => { fetchMcpServers(); fetchMcpHealth(); }}>Refresh</Button>
                <Button onClick={() => {
                  // Manual (non-tile) registration. Provider is unset —
                  // admin picks one from the dropdown that the credential
                  // section renders, which then filters the vault picker.
                  setShowAddServer(true);
                  setMcpForm({
                    server_id: "", name: "", url: "", description: "",
                    connection_type: "http", package: "",
                    provider: "", credential_id: "",
                  });
                }}>+ Add Server</Button>
              </div>
            </div>

            {/* How it works */}
            <div style={{
              background: `${COLORS.accent}08`, border: `1px solid ${COLORS.accent}20`, borderRadius: 12,
              padding: "16px 20px", marginBottom: 24, fontSize: 12, color: COLORS.textMuted, lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.accentText, marginBottom: 8 }}>How Integrations Work</div>
              <div>Integrations connect your AI agents to external apps like Slack, Jira, Salesforce, etc. Once connected, your agents can send messages, create tickets, look up records, and more — all through natural conversation.</div>
              <div style={{ marginTop: 12, padding: "12px 16px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Getting Started (3 steps)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.accentText, minWidth: 24 }}>1</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Connect the app</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Use Quick Connect below or register manually. You'll need API credentials from the app (e.g., a Slack Bot Token from api.slack.com).</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.accentText, minWidth: 24 }}>2</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Assign to an agent</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Open Agent Builder {"\u2192"} Tools & Integrations node {"\u2192"} check the integration. Save the agent.</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.accentText, minWidth: 24 }}>3</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Talk to your agent</div>
                      <div style={{ fontSize: 11, color: COLORS.textMuted }}>Just ask in plain English: "Send a message to #general saying Hello" or "Search Slack for deployment updates". The agent handles the rest.</div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 6, background: `${COLORS.orange}08`, border: `1px solid ${COLORS.orange}15`, fontSize: 11, color: COLORS.orange }}>
                  <strong>Tip:</strong> For Slack, remember to invite the bot to each channel first (/invite @BotName). The bot can only access channels it's been invited to.
                </div>
              </div>
            </div>

            {/* ─── G-2: Infrastructure panel ──────────────────────── */}
            {(() => {
              const phase = mcpInfra?.status?.phase;
              const exists = !!mcpInfra?.exists;
              const offline = !!mcpInfra?.offline;
              const phaseColor = {
                Ready: COLORS.green,
                Provisioning: COLORS.accent,
                Pending: COLORS.textMuted,
                Upgrading: COLORS.orange,
                Failed: "#ef4444",
                Deleting: "#ef4444",
              }[phase] || COLORS.textMuted;
              return (
                <div style={{
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
                  padding: "16px 20px", marginBottom: 16,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>MCP Infrastructure</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {!exists && !offline && (
                        <Button onClick={handleProvisionMcp} disabled={mcpProvisioning} style={{ fontSize: 11 }}>
                          {mcpProvisioning ? "Provisioning…" : "Provision"}
                        </Button>
                      )}
                      {exists && (
                        <Button variant="secondary" onClick={handleTriggerReconcile} style={{ fontSize: 11 }}>
                          Retry reconcile
                        </Button>
                      )}
                      {exists && (
                        <Button variant="secondary" onClick={handleReconcileBifrostNow} disabled={mcpReconcilingNow} style={{ fontSize: 11 }}>
                          {mcpReconcilingNow ? "Reconciling…" : "Reconcile Bifrost"}
                        </Button>
                      )}
                      <Button variant="secondary" onClick={fetchMcpInfra} disabled={mcpInfraLoading} style={{ fontSize: 11 }}>
                        {mcpInfraLoading ? "…" : "Refresh"}
                      </Button>
                    </div>
                  </div>

                  {offline ? (
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                      Kubernetes API unreachable — per-tenant Bifrost status unavailable. Chat still works via the shared Bifrost.
                    </div>
                  ) : !exists ? (
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                      This tenant doesn't have a dedicated Bifrost yet. MCP calls currently route to the shared Bifrost. Click <strong>Provision</strong> to create a per-tenant instance.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(2, 1fr)", gap: 8, fontSize: 12 }}>
                      <div>
                        <span style={{ color: COLORS.textMuted }}>Phase: </span>
                        <span style={{ color: phaseColor, fontWeight: 600 }}>{phase || "—"}</span>
                        {(mcpInfra?.status?.readyReplicas !== undefined) && (
                          <span style={{ color: COLORS.textMuted, marginLeft: 8 }}>
                            ({mcpInfra.status.readyReplicas} replica{mcpInfra.status.readyReplicas !== 1 ? "s" : ""})
                          </span>
                        )}
                      </div>
                      <div>
                        <span style={{ color: COLORS.textMuted }}>Version: </span>
                        <span style={{ color: COLORS.text }}>{mcpInfra?.status?.bifrostVersion || mcpInfra?.spec?.bifrostImage || "—"}</span>
                      </div>
                      <div style={{ gridColumn: bp.isMobile ? "auto" : "1 / -1" }}>
                        <span style={{ color: COLORS.textMuted }}>Endpoint: </span>
                        <span style={{ color: COLORS.text, fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>
                          {mcpInfra?.status?.endpoint || "—"}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: COLORS.textMuted }}>Certificate: </span>
                        <span style={{ color: mcpInfra?.status?.certificateReady ? COLORS.green : COLORS.orange }}>
                          {mcpInfra?.status?.certificateReady ? "Ready" : "Not ready"}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: COLORS.textMuted }}>Last reconciled: </span>
                        <span style={{ color: COLORS.text }}>{mcpInfra?.status?.lastReconciled ? new Date(mcpInfra.status.lastReconciled).toLocaleString() : "—"}</span>
                      </div>
                      {mcpInfra?.status?.conditions?.[0]?.message && (
                        <div style={{ gridColumn: bp.isMobile ? "auto" : "1 / -1", fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                          {mcpInfra.status.conditions[0].message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ─── G-3: Credentials panel ────────────────────────── */}
            <div style={{
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
              padding: "16px 20px", marginBottom: 16,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>Credentials vault</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                    Shared credentials — one entry can power multiple servers. Rotating here re-registers every linked server.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="secondary" onClick={fetchCredentials} disabled={credentialsLoading} style={{ fontSize: 11 }}>
                    {credentialsLoading ? "…" : "Refresh"}
                  </Button>
                  <Button onClick={() => {
                    setCredEditingId(null);
                    setCredForm({ name: "", provider: "generic_api_key", description: "", values: {} });
                    setShowAddCredential(true);
                  }} style={{ fontSize: 11 }}>+ New credential</Button>
                </div>
              </div>

              {credResult && (
                <div style={{
                  padding: "8px 12px", borderRadius: 6, marginBottom: 10, fontSize: 11,
                  background: credResult.type === "success" ? `${COLORS.green}12` : "#ef444412",
                  border: `1px solid ${credResult.type === "success" ? COLORS.green : "#ef4444"}33`,
                  color: credResult.type === "success" ? COLORS.green : "#ef4444",
                }}>{credResult.message}</div>
              )}

              {credentials.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "12px 0" }}>
                  No shared credentials yet. Create one to reuse across servers.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {credentials.map(c => (
                    <div key={c.id} style={{
                      border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 14px",
                      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                          {c.provider} · {c.field_count} field{c.field_count !== 1 ? "s" : ""} · used by {c.dependent_count} server{c.dependent_count !== 1 ? "s" : ""}
                          {c.last_used_at && ` · last used ${new Date(c.last_used_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Button variant="secondary" onClick={() => handleEditCredentialStart(c)} style={{ fontSize: 11 }}>Edit</Button>
                        <Button
                          variant="secondary"
                          onClick={() => handleDeleteCredential(c.id)}
                          disabled={credDeleting === c.id}
                          style={{ fontSize: 11, color: "#ef4444", borderColor: "#ef444440" }}
                        >
                          {credDeleting === c.id ? "…" : "Delete"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add / edit credential form */}
              {showAddCredential && (() => {
                const providerDef = credProviders.find(p => p.id === credForm.provider);
                const fields = providerDef?.fields || [];
                // Phase E1 — provider may declare credential_type='oauth_managed'.
                // Field bindings then target oauth_config instead of values.
                const isOauthProvider = providerDef?.credential_type === "oauth_managed";
                const fieldBag = isOauthProvider ? (credForm.oauth_config || {}) : (credForm.values || {});
                const setField = (key, val) => setCredForm(f => isOauthProvider
                  ? ({ ...f, oauth_config: { ...(f.oauth_config || {}), [key]: val }, credential_type: "oauth_managed" })
                  : ({ ...f, values: { ...(f.values || {}), [key]: val }, credential_type: "static" })
                );
                return (
                  <div style={{
                    marginTop: 12, padding: 14, borderRadius: 10,
                    background: `${COLORS.accent}06`, border: `1px solid ${COLORS.accent}25`,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, marginBottom: 10 }}>
                      {credEditingId ? "Edit credential" : "New credential"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "1fr 1fr", gap: 8, marginBottom: 10 }}>
                      <div>
                        <Label style={{ fontSize: 11 }}>Name</Label>
                        <Input
                          placeholder="Production Slack workspace"
                          value={credForm.name}
                          onChange={e => setCredForm(f => ({ ...f, name: e.target.value }))}
                          style={{ fontSize: 12 }}
                        />
                      </div>
                      <div>
                        <Label style={{ fontSize: 11 }}>Provider</Label>
                        <select
                          value={credForm.provider}
                          onChange={e => setCredForm(f => ({ ...f, provider: e.target.value, values: {} }))}
                          disabled={!!credEditingId}
                          style={{
                            width: "100%", padding: "8px 10px", fontSize: 12,
                            background: COLORS.surface, color: COLORS.text, border: `1px solid ${COLORS.border}`, borderRadius: 6,
                          }}
                        >
                          {credProviders.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <Label style={{ fontSize: 11 }}>Description (optional)</Label>
                      <Input
                        placeholder="Used for order-notification agent"
                        value={credForm.description}
                        onChange={e => setCredForm(f => ({ ...f, description: e.target.value }))}
                        style={{ fontSize: 12 }}
                      />
                    </div>
                    {fields.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                        {fields.map(f => {
                          // In edit mode, the backend returns masked values like "••••abcd".
                          // Rendered inside a password input they get obfuscated again into
                          // plain dots — user loses the fingerprint. Detect the mask and
                          // show a status badge + fingerprint beside the label instead.
                          // Phase E1 — bag is values (static) or oauth_config (oauth_managed).
                          const currentVal = (fieldBag[f.key] || "");
                          const isMasked = credEditingId && f.type === "password"
                            && typeof currentVal === "string"
                            && (currentVal.startsWith("••••") || currentVal.startsWith("***"));
                          return (
                            <div key={f.key}>
                              <Label style={{ fontSize: 11 }}>
                                {f.label}{" "}
                                <span style={{ color: COLORS.textMuted, fontFamily: "monospace", fontSize: 10 }}>{f.key}</span>
                                {isMasked && (
                                  <span style={{ marginLeft: 8, padding: "1px 6px", fontSize: 9, fontWeight: 600, borderRadius: 4, background: `${COLORS.green}20`, color: COLORS.green, border: `1px solid ${COLORS.green}40`, letterSpacing: "0.04em", fontFamily: "monospace" }}>
                                    ✓ SET · {currentVal}
                                  </span>
                                )}
                              </Label>
                              <Input
                                type={f.type === "password" ? "password" : "text"}
                                placeholder={
                                  isMasked
                                    ? "Leave blank to keep the existing value; type new value to rotate"
                                    : (f.placeholder || "")
                                }
                                value={isMasked ? "" : currentVal}
                                onChange={e => setField(f.key, e.target.value)}
                                style={{ fontSize: 12 }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10, padding: 8, borderRadius: 6, background: COLORS.surface }}>
                        {credForm.provider === "generic_headers"
                          ? "This provider has no required fields. Add custom key/value pairs below (or configure directly on the MCP server form)."
                          : "Select a provider to see its required fields."}
                      </div>
                    )}
                    {credEditingId && (
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 10 }}>
                        Leave password fields showing <code>••••••••</code> to keep the existing secret. Re-type to rotate — this will re-register every dependent server.
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button onClick={handleSaveCredential} disabled={credSaving || !credForm.name} style={{ fontSize: 12 }}>
                        {credSaving ? "Saving…" : credEditingId ? "Save changes" : "Create credential"}
                      </Button>
                      <Button variant="secondary" onClick={() => {
                        setShowAddCredential(false);
                        setCredEditingId(null);
                        setCredForm({ name: "", provider: "generic_api_key", description: "", values: {}, credential_type: "static", oauth_config: null });
                        setCredOauthResult(null);
                      }} style={{ fontSize: 12 }}>Cancel</Button>
                      {/* Phase E1 — OAuth Test Refresh — only when editing an oauth_managed credential. */}
                      {credEditingId && isOauthProvider && (
                        <Button
                          variant="secondary"
                          onClick={async () => {
                            setCredOauthTesting(credEditingId);
                            setCredOauthResult(null);
                            try {
                              const res = await credentialsApi.testOauthRefresh(credEditingId);
                              if (res.success) {
                                setCredOauthResult({
                                  type: "success",
                                  message: `OAuth refresh OK · expires ${res.data.expires_at || "unknown"}`,
                                });
                              }
                            } catch (err) {
                              const msg = err.detail?.error || err.message || "Refresh failed";
                              setCredOauthResult({ type: "error", message: msg });
                            } finally {
                              setCredOauthTesting(null);
                            }
                          }}
                          disabled={!!credOauthTesting}
                          style={{ fontSize: 12 }}
                        >
                          {credOauthTesting === credEditingId ? "Testing…" : "Test refresh"}
                        </Button>
                      )}
                    </div>
                    {credOauthResult && (
                      <div style={{
                        marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 12,
                        background: credOauthResult.type === "success" ? `${COLORS.green}12` : `${(COLORS.red || "#ef4444")}12`,
                        color: credOauthResult.type === "success" ? COLORS.green : (COLORS.red || "#ef4444"),
                        border: `1px solid ${credOauthResult.type === "success" ? `${COLORS.green}30` : `${(COLORS.red || "#ef4444")}30`}`,
                      }}>
                        {credOauthResult.message}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Allowed External Hosts section removed 2026-04-24.
                Backend endpoints (GET/PUT /api/mcp/allowlist) still exist but
                no registration code path validates against the stored hosts.
                Surfacing an unenforced security control was misleading admins
                and implied a 9-host cap that never existed. If we re-introduce
                this, we need the backend gate first — see mcp-remediation-v1
                follow-ups. */}

            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
              {[
                { label: "Total Servers", count: mcpServers.length, iconName: "node.mcp", color: COLORS.accentText },
                { label: "Healthy", count: Object.values(mcpHealth).filter(h => h.status === "healthy").length, iconName: "status.ok", color: COLORS.green },
                { label: "Unhealthy", count: Object.values(mcpHealth).filter(h => h.status !== "healthy").length, iconName: "status.warning", color: COLORS.orange },
              ].map(s => (
                <div key={s.label} style={{
                  background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(),
                  padding: "16px 20px", display: "flex", alignItems: "center", gap: 14,
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: `${s.color}12`, border: `1px solid ${s.color}25`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name={s.iconName} size={20} color={s.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.text }}>{s.count}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Server list */}
            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: bp.isMobile ? "16px 12px" : "20px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 16 }}>Registered Servers</div>
              {mcpLoading ? (
                <div style={{ fontSize: 12, color: COLORS.textMuted, padding: "32px 0", textAlign: "center" }}>Loading servers...</div>
              ) : mcpServers.length === 0 ? (
                <div style={{ padding: "32px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{"\uD83D\uDD0C"}</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>No MCP servers registered</div>
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>Add a tool server to enable external tool access for your agents</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {/* Header */}
                  <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1.5fr 1fr 0.6fr" : "1.2fr 1.5fr 1.5fr 0.8fr 0.8fr", gap: 0 }}>
                    {(bp.isMobile ? ["Name", "Status", ""] : ["Name", "Description", "Config", "Status", ""]).map(h => (
                      <div key={h || "actions"} style={{
                        fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.06em",
                        textTransform: "uppercase", padding: "0 0 12px 0", borderBottom: `1px solid ${COLORS.border}`,
                      }}>{h || "Actions"}</div>
                    ))}
                  </div>
                  {/* Rows */}
                  {mcpServers.map(server => {
                    const health = mcpHealth[server.server_id];
                    // Five-state badge logic — 2026-05-04 fix for "Healthy · 0 tools"
                    // confusion. Bifrost can report a client as `connected` even
                    // when its subprocess crashed during tools/list (the
                    // canonical Slack-token-rejected case), so any healthy-state
                    // result with tool_count == 0 is downgraded to "Degraded".
                    // Backend also returns status='degraded' directly; this
                    // local downgrade is belt-and-braces in case the backend
                    // is older than 053.
                    const rawStatus = health?.status;
                    const toolCount = health?.tool_count ?? 0;
                    const effectiveStatus = (
                      rawStatus === "healthy" && toolCount === 0
                        ? "degraded"
                        : rawStatus
                    );
                    const isHealthy = effectiveStatus === "healthy";
                    const isDegraded = effectiveStatus === "degraded";
                    const statusColor = health
                      ? (isHealthy
                          ? COLORS.green
                          : (isDegraded ? (COLORS.orange || "#f59e0b") : COLORS.pink))
                      : COLORS.textDim;
                    const statusLabel = health
                      ? (isHealthy
                          ? "Healthy"
                          : (isDegraded
                              ? `Degraded · ${toolCount} tools`
                              : (effectiveStatus === "not_registered" ? "Not registered" : "Unhealthy")))
                      : "Unknown";
                    return (
                      <div key={server.server_id} style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1.5fr 1fr 0.6fr" : "1.2fr 1.5fr 1.5fr 0.8fr 0.8fr", gap: 0 }}>
                        <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: 8,
                            background: `${COLORS.accent}15`, border: `1px solid ${COLORS.accent}25`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 13,
                          }}>{"\uD83D\uDD0C"}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{server.name}</div>
                            <div style={{ fontSize: 10, color: COLORS.textDim }}>{server.server_id}</div>
                          </div>
                        </div>
                        {!bp.isMobile && (
                          <>
                            <div style={{ padding: "14px 0", fontSize: 12, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                              {server.description || "\u2014"}
                            </div>
                            <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                              {(() => {
                                // Vault-only display. Three states:
                                //   1. credential_id → resolve to vault entry → "🔗 <name>"
                                //   2. credential_id pointing at a deleted vault entry → red badge
                                //   3. no credential_id AND no inline config → orange "No credentials"
                                //   4. no credential_id BUT legacy inline config still present → migration badge
                                const linkedCred = server.credential_id
                                  ? (credentials || []).find(c => c.id === server.credential_id)
                                  : null;
                                if (linkedCred) {
                                  return (
                                    <span style={{
                                      fontSize: 10, padding: "2px 6px", borderRadius: 4,
                                      background: `${COLORS.accent}12`, color: COLORS.accent,
                                      border: `1px solid ${COLORS.accent}20`,
                                    }}>🔗 {linkedCred.name}</span>
                                  );
                                }
                                if (server.credential_id) {
                                  return (
                                    <span style={{
                                      fontSize: 10, padding: "2px 6px", borderRadius: 4,
                                      background: `${COLORS.pink || "#ef4444"}12`,
                                      color: COLORS.pink || "#ef4444",
                                      border: `1px solid ${COLORS.pink || "#ef4444"}30`,
                                    }} title="Linked credential was deleted from the vault">
                                      ❗ vault entry deleted
                                    </span>
                                  );
                                }
                                if (server.has_config) {
                                  // Legacy inline-config server (predates vault-only).
                                  // Re-register via the wizard to migrate.
                                  return (
                                    <span style={{
                                      fontSize: 10, padding: "2px 6px", borderRadius: 4,
                                      background: `${COLORS.orange || "#f59e0b"}14`,
                                      color: COLORS.orange || "#f59e0b",
                                      border: `1px solid ${COLORS.orange || "#f59e0b"}30`,
                                    }} title="Legacy inline credentials. Re-register through the vault to migrate.">
                                      ⚠ legacy inline
                                    </span>
                                  );
                                }
                                return (
                                  <span style={{ fontSize: 11, color: COLORS.orange }}>⚠ No credentials</span>
                                );
                              })()}
                            </div>
                          </>
                        )}
                        <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{
                              width: 7, height: 7, borderRadius: "50%",
                              background: statusColor,
                              boxShadow: `0 0 5px ${statusColor}66`,
                            }} />
                            <span style={{ fontSize: 11, color: statusColor, fontWeight: 500 }}>{statusLabel}</span>
                            {/* Reconciler-state sub-badge: Quarantined / Backoff.
                                Populated from mcp_servers columns (migration 035)
                                so admins can tell "retry pending" from "manually
                                halt required". */}
                            {server.state === "quarantined" && (
                              <Badge color={COLORS.pink} style={{ fontSize: 9, padding: "1px 5px" }}>Quarantined</Badge>
                            )}
                            {server.state !== "quarantined" && server.backoff_until && new Date(server.backoff_until) > new Date() && (
                              <Badge color={COLORS.orange} style={{ fontSize: 9, padding: "1px 5px" }}>
                                Retry {new Date(server.backoff_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </Badge>
                            )}
                            {server.consecutive_failures > 0 && (
                              <span style={{ fontSize: 10, color: COLORS.textDim }}>
                                ({server.consecutive_failures} fail{server.consecutive_failures !== 1 ? "s" : ""})
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ padding: "14px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                          <Button size="sm" variant="secondary" onClick={() => handleDiscoverTools(server.server_id)}
                            disabled={mcpToolsLoading === server.server_id}>
                            {mcpToolsLoading === server.server_id
                              ? "..."
                              : (mcpTools[server.server_id] ? "Hide Tools" : "Tools")}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => handleEditServer(server)}>
                            Edit
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => handleDeleteServer(server.server_id)} disabled={mcpDeleting === server.server_id}>
                            {mcpDeleting === server.server_id ? "..." : "Remove"}
                          </Button>
                        </div>
                        {/* Diagnostics panel — surfaces last_error from the
                            reconciler whenever the server is unhealthy or has
                            any recorded failures. Built-in scrubbing on the
                            backend side means tokens never appear here. */}
                        {/* Diagnostics: surface last_error whenever the row
                            isn't fully healthy. Includes the new `degraded`
                            state (Bifrost connected but 0 tools — usually a
                            credential rejection or package compat problem).
                            For degraded with NO last_error in the DB, surface
                            an explanatory note pointing at the most likely
                            causes so the admin has a starting point. */}
                        {(server.last_error || isDegraded) && (server.state === "quarantined" || server.consecutive_failures > 0 || !isHealthy) && (
                          <div style={{
                            gridColumn: "1 / -1",
                            padding: "10px 14px",
                            background: isDegraded
                              ? `${(COLORS.orange || "#f59e0b")}08`
                              : `${COLORS.pink}08`,
                            border: `1px solid ${
                              isDegraded
                                ? (COLORS.orange || "#f59e0b") + "20"
                                : COLORS.pink + "20"
                            }`,
                            borderRadius: 6,
                            margin: "0 0 6px",
                          }}>
                            <div style={{
                              fontSize: 10, fontWeight: 700,
                              color: isDegraded ? (COLORS.orange || "#f59e0b") : COLORS.pink,
                              textTransform: "uppercase", letterSpacing: "0.06em",
                              marginBottom: 4,
                            }}>
                              {isDegraded && !server.last_error
                                ? "0 tools discovered — subprocess started but reported no tools"
                                : `Last error ${server.last_attempt_at ? `(${new Date(server.last_attempt_at).toLocaleString()})` : ""}`}
                            </div>
                            <pre style={{
                              fontSize: 11, color: COLORS.textMuted,
                              whiteSpace: "pre-wrap", wordBreak: "break-word",
                              margin: 0, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                              maxHeight: 200, overflowY: "auto",
                            }}>{server.last_error || (
                              "Common causes:\n" +
                              "  • Credential rejected by upstream (wrong token, missing scopes, expired)\n" +
                              "  • Package mismatch (npm package name differs from MCP server's expected env vars)\n" +
                              "  • Subprocess crashed during tools/list — check Bifrost pod logs for the server's stderr\n\n" +
                              "Hint: kubectl logs -n agentic <bifrost-pod> -c bifrost | grep " + server.server_id
                            )}</pre>
                          </div>
                        )}
                        {/* Discovered tools panel */}
                        {mcpTools[server.server_id] && (() => {
                          const { tools: srvTools = [], server_info: srvInfo = {} } = mcpTools[server.server_id];
                          return (
                          <div style={{ gridColumn: "1 / -1", padding: "12px 16px", background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
                            {/* Connected instance info */}
                            {(srvInfo.workspace_name || srvInfo.workspace_domain) && (
                              <div style={{
                                display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                                padding: "8px 12px", borderRadius: 8, background: `${COLORS.green}08`,
                                border: `1px solid ${COLORS.green}20`,
                              }}>
                                {srvInfo.workspace_icon && <img src={srvInfo.workspace_icon} alt="" style={{ width: 20, height: 20, borderRadius: 4 }} />}
                                <span style={{ fontSize: 12, color: COLORS.green, fontWeight: 600 }}>
                                  Connected to: {srvInfo.workspace_name || srvInfo.workspace_domain}
                                </span>
                                {srvInfo.workspace_domain && (
                                  <span style={{ fontSize: 11, color: COLORS.textDim }}>({srvInfo.workspace_domain}.slack.com)</span>
                                )}
                              </div>
                            )}
                            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.accentText, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              {srvTools.length} Tool{srvTools.length !== 1 ? "s" : ""} Available
                            </div>
                            {srvTools.length === 0 ? (
                              <div style={{ fontSize: 12, color: COLORS.textMuted }}>No tools discovered. Check server health.</div>
                            ) : (
                              <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "1fr 1fr", gap: 8 }}>
                                {srvTools.map(tool => (
                                  <div key={tool.name} style={{
                                    padding: "10px 14px", borderRadius: 8, background: COLORS.surface,
                                    border: `1px solid ${COLORS.border}`,
                                  }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, fontFamily: "monospace" }}>{tool.name}</div>
                                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{tool.description}</div>
                                    {tool.inputSchema?.properties && (
                                      <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                        {Object.keys(tool.inputSchema.properties).map(p => (
                                          <span key={p} style={{
                                            fontSize: 10, padding: "2px 6px", borderRadius: 4,
                                            background: `${COLORS.accent}12`, color: COLORS.accentText,
                                            border: `1px solid ${COLORS.accent}20`,
                                          }}>{p}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick-Connect tile grid removed 2026-05-04. The previous
                vendor-specific tiles (slack, github, salesforce_devtools)
                hardcoded one specific implementation choice per vendor and
                routinely led admins into mismatches with their actual
                token type / auth flow. The "+ Add Server" button at the
                top of this tab now opens the generic register form,
                which uses Phase E1's URL/header templating + three
                generic credential primitives (generic_api_key,
                generic_headers, generic_oauth) to cover every MCP server
                — including Slack's hosted MCP at https://mcp.slack.com/mcp
                via OAuth-managed credentials. */}

            {/* Add Server Modal */}
            <Modal open={showAddServer} onClose={() => setShowAddServer(false)} title="Register Integration" width={500}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Input
                  label="Server ID"
                  value={mcpForm.server_id}
                  onChange={e => setMcpForm(f => ({ ...f, server_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                  placeholder="e.g. salesforce, jira, my-api"
                />
                <Input
                  label="Name"
                  value={mcpForm.name}
                  onChange={e => setMcpForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Salesforce CRM"
                />
                <Input
                  label="Description"
                  value={mcpForm.description}
                  onChange={e => setMcpForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this integration do?"
                />

                {/* Connection type selector — hidden for Quick Connect (pre-filled) */}
                {!mcpForm.configFields?.length && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textMuted, marginBottom: 6 }}>Connection Type</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[
                        { value: "stdio", label: "Local (STDIO)", desc: "npm / Python / Docker / binary" },
                        { value: "http", label: "HTTP URL", desc: "Remote MCP endpoint" },
                        { value: "sse", label: "SSE URL", desc: "Server-Sent Events" },
                      ].map(opt => (
                        <button key={opt.value} onClick={() => setMcpForm(f => ({ ...f, connection_type: opt.value }))}
                          style={{
                            flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${mcpForm.connection_type === opt.value ? COLORS.accent : COLORS.border}`,
                            background: mcpForm.connection_type === opt.value ? `${COLORS.accent}12` : COLORS.surface,
                            color: mcpForm.connection_type === opt.value ? COLORS.accent : COLORS.textMuted,
                            fontSize: 11, fontWeight: 600, cursor: "pointer", textAlign: "center",
                          }}>
                          <div>{opt.label}</div>
                          <div style={{ fontSize: 9, fontWeight: 400, marginTop: 2, opacity: 0.7 }}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STDIO: runtime selector + runtime-specific fields (MCP-UI-1) */}
                {(mcpForm.connection_type || "stdio") === "stdio" && (
                  <>
                    {/* One-click presets for common server shapes */}
                    {stdioPresets.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textMuted, marginBottom: 6 }}>Quick start</div>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {stdioPresets.map(p => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => setMcpForm(f => ({ ...f, ...applyStdioPreset(p) }))}
                              style={{
                                padding: "6px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                                background: COLORS.surface, color: COLORS.text, fontSize: 11,
                                fontWeight: 600, cursor: "pointer",
                              }}
                            >{p.label}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Runtime */}
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textMuted, marginBottom: 6 }}>Runtime</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[
                          { value: "npx", label: "NPM (npx)" },
                          { value: "uvx", label: "Python (uvx)" },
                          { value: "docker", label: "Docker" },
                          { value: "custom", label: "Custom" },
                        ].map(opt => (
                          <button key={opt.value} type="button" onClick={() => setMcpForm(f => ({ ...f, stdio_runtime: opt.value }))}
                            style={{
                              flex: 1, padding: "7px 8px", borderRadius: 8,
                              border: `1px solid ${(mcpForm.stdio_runtime || "npx") === opt.value ? COLORS.accent : COLORS.border}`,
                              background: (mcpForm.stdio_runtime || "npx") === opt.value ? `${COLORS.accent}12` : COLORS.surface,
                              color: (mcpForm.stdio_runtime || "npx") === opt.value ? COLORS.accent : COLORS.textMuted,
                              fontSize: 11, fontWeight: 600, cursor: "pointer",
                            }}>{opt.label}</button>
                        ))}
                      </div>
                    </div>

                    {/* Package (npx / uvx) */}
                    {(["npx", "uvx"].includes(mcpForm.stdio_runtime || "npx")) && (
                      <Input
                        label={(mcpForm.stdio_runtime || "npx") === "uvx" ? "Python package (uvx)" : "NPM package (npx)"}
                        value={mcpForm.package || ""}
                        onChange={e => setMcpForm(f => ({ ...f, package: e.target.value }))}
                        placeholder={(mcpForm.stdio_runtime || "npx") === "uvx" ? "e.g. postgres-mcp" : "e.g. @henkey/postgres-mcp-server"}
                        helper={(mcpForm.stdio_runtime || "npx") === "uvx"
                          ? "For reference only — you must ALSO type the package name (and any flags, e.g. a version pin or --access-mode) into Arguments below, in the exact order the tool expects."
                          : undefined}
                      />
                    )}

                    {/* Command (custom only) */}
                    {mcpForm.stdio_runtime === "custom" && (
                      <Input
                        label="Command"
                        value={mcpForm.command || ""}
                        onChange={e => setMcpForm(f => ({ ...f, command: e.target.value }))}
                        placeholder="e.g. /usr/local/bin/my-mcp-server"
                      />
                    )}

                    {/* Arguments (uvx / docker / custom) — one per line */}
                    {(["uvx", "docker", "custom"].includes(mcpForm.stdio_runtime)) && (
                      <div>
                        <Label style={{ fontSize: 11 }}>
                          Arguments <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>
                            (one per line{mcpForm.stdio_runtime === "docker" ? ", e.g. run / --rm / -i / <image>"
                              : mcpForm.stdio_runtime === "uvx" ? " — include the package name itself, in position"
                              : ""})
                          </span>
                        </Label>
                        <textarea
                          value={mcpForm.args_text || ""}
                          onChange={e => setMcpForm(f => ({ ...f, args_text: e.target.value }))}
                          placeholder={mcpForm.stdio_runtime === "docker" ? "run\n--rm\n-i\nmcp/postgres"
                            : mcpForm.stdio_runtime === "uvx" ? "--with\nmcp<2\npostgres-mcp==0.3.0\n--access-mode=restricted"
                            : "--flag\nvalue"}
                          rows={3}
                          style={{
                            width: "100%", padding: "8px 10px", fontSize: 12,
                            fontFamily: "ui-monospace, SFMono-Regular, monospace",
                            borderRadius: 6, border: `1px solid ${COLORS.border}`,
                            background: COLORS.surface, color: COLORS.text, resize: "vertical",
                          }}
                        />
                      </div>
                    )}

                    {/* Env-var guidance from a preset — which keys the server needs */}
                    {mcpForm.presetEnvKeys?.length > 0 && (
                      <div style={{
                        fontSize: 11, color: COLORS.textMuted, background: `${COLORS.accent}0c`,
                        border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 10px",
                      }}>
                        <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
                          Environment variables this server needs
                        </div>
                        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                          {mcpForm.presetEnvKeys.join(" · ")}
                        </div>
                        <div style={{ marginTop: 6 }}>
                          {mcpForm.presetHint || `Create a "${mcpForm.presetCredentialProvider || "generic_env"}" credential with these keys, then link it below.`}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* HTTP/SSE: URL */}
                {(mcpForm.connection_type === "http" || mcpForm.connection_type === "sse") && (
                  <>
                    <Input
                      label="Server URL"
                      value={mcpForm.url || ""}
                      onChange={e => setMcpForm(f => ({ ...f, url: e.target.value }))}
                      placeholder="e.g. https://mcp.example.com/tools"
                    />
                    {/* Phase E1 — URL template. Optional; renders ${VAR} from the linked
                        credential at registration time. Useful for upstreams that put
                        the API key in the URL query string (e.g. Alpha Vantage). */}
                    <div style={{ marginTop: 10 }}>
                      <Label style={{ fontSize: 11 }}>URL template <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>(optional, ${"${VAR}"} substituted at registration)</span></Label>
                      <Input
                        value={mcpForm.url_template || ""}
                        onChange={e => setMcpForm(f => ({ ...f, url_template: e.target.value }))}
                        placeholder="e.g. https://mcp.alphavantage.co/mcp?apikey=${API_KEY}"
                      />
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                        Wins over Server URL at registration. Variables are resolved from the linked credential's fields.
                        Use <code>${"${ACCESS_TOKEN}"}</code> for OAuth-managed credentials.
                      </div>
                    </div>
                    {/* Phase E1 — header template (JSON). Optional; for upstreams
                        with custom auth headers (e.g. {"Authorization": "Bearer ${ACCESS_TOKEN}"}). */}
                    <div style={{ marginTop: 10 }}>
                      <Label style={{ fontSize: 11 }}>Header template (JSON, optional)</Label>
                      <textarea
                        value={mcpForm.header_template_text || ""}
                        onChange={e => setMcpForm(f => ({ ...f, header_template_text: e.target.value }))}
                        placeholder={'e.g. {"Authorization": "Bearer ${ACCESS_TOKEN}"}'}
                        rows={3}
                        style={{
                          width: "100%", padding: "8px 10px", fontSize: 12,
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                          borderRadius: 6, border: `1px solid ${COLORS.border}`,
                          background: COLORS.surface, color: COLORS.text,
                          resize: "vertical",
                        }}
                      />
                      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                        Each header value supports <code>${"${VAR}"}</code> substitution. Header names are literal.
                      </div>
                    </div>
                  </>
                )}

                {/* Credentials section — vault is the single rotation point.
                    UX:
                      - Tile-driven flow: provider is pre-set; show vault picker
                        filtered by it.
                      - Manual flow (+ Add Server): provider is empty; show a
                        Provider dropdown FIRST so the admin tells us which
                        catalog of vault entries to filter to.
                      - Provider with no fields (e.g. URL-only HTTP MCP):
                        message that no credentials are needed; no picker.
                */}
                {(() => {
                  const providerDef = mcpForm.provider
                    ? (credProviders || []).find(p => p.id === mcpForm.provider)
                    : null;
                  const providerHasFields = providerDef
                    ? (providerDef.fields || []).length > 0
                    : true;  // unknown until provider picked
                  const providerCreds = mcpForm.provider
                    ? (credentials || []).filter(c => c.provider === mcpForm.provider)
                    : [];
                  const selected = providerCreds.find(c => c.id === mcpForm.credential_id);
                  return (
                    <div style={{ marginTop: 4, paddingTop: 12, borderTop: `1px solid ${COLORS.border}` }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
                        Credentials
                      </div>

                      {/* Provider picker — always shown, but disabled when the
                          tile flow already pinned a provider (admin can't
                          change it for tile-driven registrations). */}
                      <div style={{ marginTop: 8 }}>
                        <Label style={{ fontSize: 11 }}>Provider</Label>
                        <select
                          value={mcpForm.provider || ""}
                          onChange={e => setMcpForm(f => ({ ...f, provider: e.target.value, credential_id: "" }))}
                          style={{
                            width: "100%", padding: "8px 10px", fontSize: 12,
                            borderRadius: 6, border: `1px solid ${COLORS.border}`,
                            background: COLORS.surface, color: COLORS.text,
                          }}
                        >
                          <option value="">— pick a provider —</option>
                          {(credProviders || []).map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                          Picks the credential schema. Vault entries are filtered to this provider below.
                        </div>
                      </div>

                      {/* Vault picker — only after provider is chosen */}
                      {mcpForm.provider && providerHasFields && (
                        <div style={{ marginTop: 12 }}>
                          <Label style={{ fontSize: 11 }}>Vault entry</Label>
                          <select
                            value={mcpForm.credential_id || ""}
                            onChange={e => setMcpForm(f => ({ ...f, credential_id: e.target.value }))}
                            style={{
                              width: "100%", padding: "8px 10px", fontSize: 12,
                              borderRadius: 6, border: `1px solid ${COLORS.border}`,
                              background: COLORS.surface, color: COLORS.text,
                            }}
                          >
                            <option value="">— pick a credential —</option>
                            {providerCreds.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name} · {c.field_count} field{c.field_count !== 1 ? "s" : ""} · used by {c.dependent_count} server{c.dependent_count !== 1 ? "s" : ""}
                              </option>
                            ))}
                          </select>
                          {providerCreds.length === 0 ? (
                            <div style={{ fontSize: 11, color: COLORS.pink || "#ef4444", marginTop: 6 }}>
                              No vault entries for provider <code>{mcpForm.provider}</code>. Open the <em>Credentials vault</em> panel below and click <em>+ New credential</em> with this provider, then return.
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
                              Don't see your credential? Add it via the <em>Credentials vault</em> panel below.
                            </div>
                          )}
                          {selected && (
                            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6 }}>
                              ✓ Selected <strong>{selected.name}</strong>. Rotating this credential will re-register every server that uses it.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Provider explicitly needs no credentials — drop the
                          picker entirely and tell the admin. */}
                      {mcpForm.provider && !providerHasFields && (
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 8 }}>
                          This provider needs no credentials.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* Success/Error banner */}
                {mcpRegResult && (
                  <div style={{
                    padding: "10px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500,
                    background: mcpRegResult.type === "success" ? `${COLORS.green}12` : `${COLORS.pink}12`,
                    color: mcpRegResult.type === "success" ? COLORS.green : COLORS.pink,
                    border: `1px solid ${mcpRegResult.type === "success" ? COLORS.green : COLORS.pink}25`,
                  }}>
                    {mcpRegResult.type === "success" ? "\u2713 " : "\u26A0 "}{mcpRegResult.message}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
                  <Button variant="secondary" onClick={() => { setShowAddServer(false); setMcpRegResult(null); }}>Cancel</Button>
                  <Button onClick={() => { setMcpRegResult(null); handleAddServer(); }} disabled={(() => {
                    if (mcpSaving || !mcpForm.server_id || !mcpForm.name) return true;
                    if ((mcpForm.connection_type || "stdio") === "stdio" && !stdioReady(mcpForm)) return true;
                    if ((mcpForm.connection_type === "http" || mcpForm.connection_type === "sse") && !mcpForm.url) return true;
                    // Vault-only: if the provider has fields, a credential
                    // must be picked. URL-only / no-credential providers
                    // (e.g. public HTTP MCP servers) skip this.
                    if (mcpForm.provider) {
                      const providerDef = (credProviders || []).find(p => p.id === mcpForm.provider);
                      const hasFields = (providerDef?.fields || []).length > 0;
                      if (hasFields && !mcpForm.credential_id) return true;
                    }
                    return false;
                  })()}>
                    {mcpSaving ? "Registering..." : "Register"}
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
        )}

        {adminTab === "rankings" && (() => {
          // 11-purpose taxonomy (PR-C 2026-06-21 — Option C vision split +
          // Option 2 classification split). Authoritative set lives in
          // model_resolver.VALID_PURPOSES + routers/auth._VALID_RANKING_FUNCTIONS.
          // See docs/design/llm-rankings-audit-matrix-v1.md §1.
          const RANKING_FUNCTIONS = [
            { key: "summarization", label: "Summarization & Internal Tools", desc: "Used for: analytics summaries, conversation compaction, query rewriting, text-to-SQL on document metadata, LlamaIndex sub-question decomposition, the fine-tuning judge, and the smart query router. A reliable structured-output model is more important here than peak reasoning — many calls per query." },
            { key: "embeddings", label: "Embeddings & Search", desc: "Embedding model for document ingestion AND search queries. Must be the same model for both — changing requires re-ingesting all data. Supported providers: OpenAI, Cohere (Embed v3 / v4 — multilingual default), Voyage (voyage-3-large for English MTEB; voyage-multimodal-3 for image-bearing chunks)." },
            { key: "chat", label: "Agent Chat (Default)", desc: "Default LLM for agent conversations when not set per-agent. Also used by the slow-path Fact Checker (verification of agent responses against retrieved evidence)." },
            { key: "intent", label: "Intent Classification", desc: "Lightweight model for classifying user intent + smart-routing tier classification (fast / standard / complex). Also used by the tier-2 question classifier when the heuristic tier-1 is unsure." },
            { key: "sentiment", label: "Sentiment Analysis", desc: "Lightweight model for analyzing message sentiment." },
            { key: "extraction", label: "Long-Term Memory Extraction", desc: "D-24: model that reads a closed conversation and extracts durable facts to remember about the customer (long-term memory). A cheaper/faster model is fine — it runs once per conversation close. Long-term memory stays disabled until this is configured (new tenants are seeded a sensible default)." },
            { key: "ingestion", label: "Data Ingestion", desc: "Model for document metadata extraction, summaries, catalog enrichment, entity/relationship extraction from text, and CSV/Excel schema inference." },
            { key: "ocr", label: "OCR (Image Text Extraction)", desc: "Vision-capable model for extracting text from image-based documents — uploaded images, scanned PDFs as images, chat-attached image files." },
            { key: "figure_caption", label: "Figure Caption (PDF)", desc: "Vision-capable model for captioning embedded figures cropped from PDFs. Reserved for future use — no live caller yet." },
            { key: "page_caption", label: "Page Caption (PDF Screenshots)", desc: "Vision-capable model for captioning rendered PDF page screenshots during ingestion (screenshot-dense PDFs where layout-aware OCR isn't enough)." },
            { key: "video_caption", label: "Video Caption (Keyframes)", desc: "Vision-capable model for captioning video keyframes during video ingestion." },
            { key: "voice_amd", label: "Voice AMD (Answering Machine Detection)", desc: "Fast classifier model for outbound voice calls — decides at pickup whether the line is a human or voicemail. Latency-critical (<1s). Strict: missing config fails the dispatch." },
          ];
          // BYOK-strict: provider model lists come exclusively from the live
          // /api/llm/models fetch (dynamicModels). No hardcoded fallback.
          // Each model in dynamicModels carries a `kind` ("chat" | "embedding")
          // so the rankings UI can filter the right options into chat vs
          // embedding dropdowns. Cohere appears in both; Voyage is embedding-only.
          const CHAT_PROVIDERS = ["openai", "anthropic", "vertex", "bedrock", "cohere", "deepseek"];
          const EMBEDDING_PROVIDERS = ["openai", "cohere", "voyage"];
          const _normalize = (entries) => entries
            .map(m => (typeof m === "string" ? { id: m, kind: "chat" } : m))
            .filter(m => m && m.id);
          // Batch 10: keep full model objects (with badges) per provider so the
          // rankings UI can render capability badges next to each option AND
          // filter by purpose-required badges (ocr/figure_caption/page_caption/
          // video_caption → vision-only). Previous shape was string-array-of-IDs
          // which discarded badges.
          const PROVIDER_MODELS = {};
          for (const provider of CHAT_PROVIDERS) {
            PROVIDER_MODELS[provider] = _normalize((dynamicModels || {})[provider] || [])
              .filter(m => m.kind === "chat" || (!m.kind && !m.id.includes("embed")));
          }
          // BYOM: surface local endpoints (keyed local_* in /api/llm/models, tagged
          // with endpoint_id) as rankable providers. Logic lives in rankingsModel.js
          // (this shell is coverage-excluded).
          const { chatProvidersAll: CHAT_PROVIDERS_ALL } = addLocalProviders(dynamicModels, PROVIDER_MODELS, CHAT_PROVIDERS);
          const EMBEDDING_MODELS = {};
          for (const provider of EMBEDDING_PROVIDERS) {
            const dyn = _normalize((dynamicModels || {})[provider] || []);
            const tagged = dyn.filter(m => m.kind === "embedding");
            const fallback = dyn.filter(m => !m.kind && (m.id.includes("embedding") || m.id.startsWith("embed-") || m.id.startsWith("voyage-")));
            EMBEDDING_MODELS[provider] = tagged.length ? tagged : fallback;
          }
          // BYOM: surface local endpoints that expose embedding-capable models
          // (probe-tagged kind:"embedding" + the "embedding" badge) so a local
          // model is selectable for the Embeddings purpose too — not just chat.
          const { embeddingProvidersAll: EMBEDDING_PROVIDERS_ALL } = addLocalEmbeddingProviders(dynamicModels, EMBEDDING_MODELS, EMBEDDING_PROVIDERS);
          // Per-purpose badge requirements (mirrors backend
          // PURPOSE_BADGE_REQUIREMENTS). Keep in sync with model_badges.py
          // — both ends use the same vocabulary so a model that the
          // backend would reject for a vision purpose (ocr/figure_caption/
          // page_caption/video_caption) doesn't even surface in the dropdown.
          const PURPOSE_BADGE_REQUIREMENTS = {
            embeddings: ["embedding"],
            // PR-C 2026-06-21: all 4 vision purposes need vision-capable models
            ocr: ["vision"],
            figure_caption: ["vision"],
            page_caption: ["vision"],
            video_caption: ["vision"],
            // chat / intent / sentiment / ingestion / summarization / voice_amd
            // accept any chat model (voice_amd benefits from fast small classifiers
            // but the badge system doesn't currently expose a "fast" capability tag —
            // admin picks based on the description guidance)
          };
          const filterByPurpose = (models, purposeKey) => {
            const required = PURPOSE_BADGE_REQUIREMENTS[purposeKey];
            if (!required) return models;
            return models.filter(m => {
              const badges = m.badges || [];
              return required.every(b => badges.includes(b));
            });
          };
          // Render-helper: capability badge pills as small inline chips.
          // Tooltips spell out each badge so admins understand why a model
          // surfaced or got filtered. Badges come from backend model_badges
          // (family-prefix detection — same vocabulary on both ends).
          const BADGE_LABEL = {
            vision: "Vision",
            tool_use: "Tools",
            structured_outputs: "Structured",
            extended_thinking: "Reasoning",
            prompt_caching: "Cache",
            long_context: "Long",
            embedding: "Emb",
            multimodal_embedding: "Multi-emb",
          };
          const BADGE_TOOLTIP = {
            vision: "Vision-capable: image inputs supported",
            tool_use: "Function/tool calling supported",
            structured_outputs: "Structured-output (JSON-schema) supported",
            extended_thinking: "Extended-thinking reasoning supported",
            prompt_caching: "Prompt caching supported",
            long_context: ">= 128K context window",
            embedding: "Embedding model",
            multimodal_embedding: "Multimodal embedding (text + image fusion)",
          };
          const BadgeRow = ({ badges }) => {
            if (!badges || badges.length === 0) return null;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {badges.map(b => (
                  <span
                    key={b}
                    title={BADGE_TOOLTIP[b] || b}
                    style={{
                      // Softer indigo pill — keeps brand association,
                      // legible by lifting text to accentText.
                      fontSize: 10, padding: "2px 7px", borderRadius: 8,
                      border: `1px solid ${COLORS.accent}40`,
                      background: `${COLORS.accent}10`,
                      color: COLORS.accentText,
                      fontWeight: 600, letterSpacing: "0.04em",
                    }}
                  >
                    {BADGE_LABEL[b] || b}
                  </span>
                ))}
              </div>
            );
          };

          const handleSaveRanking = async (fn) => {
            setRankingsSaving(fn);
            setRankingsSaved(null);
            try {
              const res = await authApi.updateLlmRankings(fn, llmRankings[fn] || []);
              if (res.success) setRankingsSaved(fn);
            } catch (err) {
              log.error("Failed to save ranking:", err);
            } finally {
              setRankingsSaving(null);
              setTimeout(() => setRankingsSaved(null), 2000);
            }
          };

          const handleResetRanking = async (fn) => {
            try {
              await authApi.deleteLlmRankings(fn);
              setLlmRankings(prev => { const next = { ...prev }; delete next[fn]; return next; });
            } catch (err) {
              log.error("Failed to reset ranking:", err);
            }
          };

          const updateRanking = (fn, idx, field, value) => {
            const lookup = fn === "embeddings" ? EMBEDDING_MODELS : PROVIDER_MODELS;
            setLlmRankings(prev => applyRankingUpdate(prev, fn, idx, field, value, lookup, filterByPurpose));
          };

          const addRankingEntry = (fn) => {
            const isEmbeddings = fn === "embeddings";
            const providerOrder = isEmbeddings ? EMBEDDING_PROVIDERS_ALL : CHAT_PROVIDERS_ALL;
            const lookup = isEmbeddings ? EMBEDDING_MODELS : PROVIDER_MODELS;
            setLlmRankings(prev => appendRankingEntry(prev, fn, providerOrder, lookup, filterByPurpose));
          };

          const removeRankingEntry = (fn, idx) => {
            setLlmRankings(prev => {
              const next = { ...prev };
              const list = [...(next[fn] || [])];
              list.splice(idx, 1);
              next[fn] = list;
              return next;
            });
          };

          const moveRankingEntry = (fn, idx, dir) => {
            setLlmRankings(prev => {
              const next = { ...prev };
              const list = [...(next[fn] || [])];
              const newIdx = idx + dir;
              if (newIdx < 0 || newIdx >= list.length) return prev;
              [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
              next[fn] = list;
              return next;
            });
          };

          if (rankingsLoading) return <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 13 }}>Loading rankings...</div>;

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <CloudFallbackToggleCard />
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.6 }}>
                Configure the order in which LLM providers are tried for each platform function. If the first provider fails, the system automatically falls back to the next in the list.
              </div>
              {RANKING_FUNCTIONS.map(fn => {
                const entries = llmRankings[fn.key] || [];
                const modelOptions = fn.key === "embeddings" ? EMBEDDING_MODELS : PROVIDER_MODELS;
                const providerOptions = fn.key === "embeddings" ? EMBEDDING_PROVIDERS_ALL : CHAT_PROVIDERS_ALL;

                return (
                  <div key={fn.key} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.inner, boxShadow: MATERIALS.spatialShadow(), padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{fn.label}</div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {rankingsSaved === fn.key && <span style={{ fontSize: 10, color: "#10B981" }}>Saved</span>}
                        {entries.length > 0 && (
                          <button onClick={() => handleResetRanking(fn.key)}
                            style={{ fontSize: 10, color: COLORS.textDim, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                            Reset
                          </button>
                        )}
                        <Button onClick={() => handleSaveRanking(fn.key)} disabled={rankingsSaving === fn.key}
                          style={{ padding: "4px 12px", fontSize: 10, borderRadius: 6 }}>
                          {rankingsSaving === fn.key ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 12 }}>{fn.desc}</div>

                    {entries.length === 0 && (
                      <div style={{ fontSize: 11, color: COLORS.textDim, fontStyle: "italic", marginBottom: 8 }}>
                        No ranking configured — using system default
                      </div>
                    )}

                    {entries.map((entry, idx) => {
                      // Models for the selected provider, filtered by the
                      // current row's purpose-required badges. Embedding
                      // rows show only embedding-tagged models; vision purposes
                      // (ocr/figure_caption/page_caption/video_caption) show
                      // only vision-capable models. Other purposes pass-through
                      // unfiltered.
                      const providerModelObjs = (modelOptions[entry.provider] || []);
                      const filteredModelObjs = filterByPurpose(providerModelObjs, fn.key);
                      const selectedModelObj = filteredModelObjs.find(m => m.id === entry.model)
                        || providerModelObjs.find(m => m.id === entry.model);
                      const selectedBadges = selectedModelObj?.badges || [];
                      return (
                        <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.accentText, width: 18, textAlign: "center" }}>
                              {idx + 1}
                            </span>
                            <select value={entry.provider} onChange={e => updateRanking(fn.key, idx, "provider", e.target.value)}
                              style={{
                                flex: 1, padding: "6px 10px", fontSize: 12, borderRadius: 6,
                                background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text,
                                outline: "none", cursor: "pointer", colorScheme: "dark",
                              }}>
                              {providerOptions.map(p => <option key={p} value={p} style={{ background: COLORS.surface, color: COLORS.text }}>{providerLabel(p)}</option>)}
                            </select>
                            <select value={entry.model} onChange={e => updateRanking(fn.key, idx, "model", e.target.value)}
                              style={{
                                flex: 2, padding: "6px 10px", fontSize: 12, borderRadius: 6,
                                background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text,
                                outline: "none", cursor: "pointer", colorScheme: "dark",
                              }}>
                              {filteredModelObjs.map(m => (
                                <option key={m.id} value={m.id} style={{ background: COLORS.surface, color: COLORS.text }}>
                                  {modelOptionLabel(m)}
                                </option>
                              ))}
                            </select>
                            <button onClick={() => moveRankingEntry(fn.key, idx, -1)} disabled={idx === 0}
                              style={{ background: "none", border: "none", cursor: idx === 0 ? "default" : "pointer", color: idx === 0 ? COLORS.border : COLORS.textMuted, fontSize: 14 }}>
                              &#9650;
                            </button>
                            <button onClick={() => moveRankingEntry(fn.key, idx, 1)} disabled={idx === entries.length - 1}
                              style={{ background: "none", border: "none", cursor: idx === entries.length - 1 ? "default" : "pointer", color: idx === entries.length - 1 ? COLORS.border : COLORS.textMuted, fontSize: 14 }}>
                              &#9660;
                            </button>
                            <button onClick={() => removeRankingEntry(fn.key, idx)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.pink, fontSize: 14 }}>
                              &times;
                            </button>
                          </div>
                          {/* Badge row: capability pills for the selected model. Empty
                              when no badges resolved (unknown family) — UI shows
                              nothing in that case, no "?" placeholder. */}
                          {selectedBadges.length > 0 && (
                            <div style={{ paddingLeft: 26 }}>
                              <BadgeRow badges={selectedBadges} />
                            </div>
                          )}
                          {/* Filter explainer: when the dropdown is filtered, show
                              "showing X of Y models — purpose requires Z" so admins
                              know WHY their previous pick disappeared. */}
                          {filteredModelObjs.length < providerModelObjs.length && (
                            <div style={{ paddingLeft: 26, fontSize: 10, color: COLORS.textDim, fontStyle: "italic" }}>
                              Showing {filteredModelObjs.length} of {providerModelObjs.length} models — purpose requires{" "}
                              {(PURPOSE_BADGE_REQUIREMENTS[fn.key] || []).map(b => BADGE_LABEL[b] || b).join(", ")}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {entries.length < 5 && (
                      <button onClick={() => addRankingEntry(fn.key)}
                        style={{
                          fontSize: 11, color: COLORS.accentText, background: "none", border: `1px dashed ${COLORS.border}`,
                          borderRadius: 6, padding: "6px 14px", cursor: "pointer", marginTop: 4, width: "100%",
                        }}>
                        + Add Provider
                      </button>
                    )}
                  </div>
                );
              })}
              <MigrateEmbeddingsPanel
                embeddingModels={EMBEDDING_MODELS}
                providers={EMBEDDING_PROVIDERS}
              />
            </div>
          );
        })()}

        {adminTab === "guardrails" && isAdmin && <GuardrailsSafetyTab canEdit={isAdmin} />}
        {adminTab === "compliance" && <ComplianceTab tenantId={currentTenant?.id} />}

        {adminTab === "voice_console" && <OutboundVoiceConsole />}

        {adminTab === "outbound_voice" && <OutboundVoiceTab />}

        {adminTab === "voice_trunks" && <VoiceTrunksTab />}

        {adminTab === "local_llm" && isAdmin && <LocalLLMEndpointsSection />}
        {adminTab === "model_serving" && isAdmin && <ModelServingTab />}
        {adminTab === "executable_skills" && isAdmin && <ExecutableSkillsTab />}
        {adminTab === "ml_execution" && <MLExecutionTargetsTab />}

        {adminTab === "ml_forecasting" && <MLForecastingTab />}

        {adminTab === "phone_numbers" && <PhoneNumbersTab />}

        {adminTab === "super_agent" && <SuperAgentTab />}

      </div>
      )}
      </div>
    </div>
  );
};

export default AdminView;
